/**
 * The game loop.
 *
 * Two loops, mirroring the server. Input is sampled and simulated on a fixed 20Hz
 * clock so that prediction runs exactly the steps the server will run, and
 * rendering happens as fast as the display allows and only interpolates.
 *
 * Tying movement to the render loop instead would make a 144Hz monitor move faster
 * than a 60Hz one, which is the oldest bug in real-time games.
 */

import {
  ContainerId,
  containerSize,
  toolbeltSlotFor,
  MOVE_STEP_MS,
  isCentred,
  sameTile,
  tileBeside,
  canShootFrom,
  haltAtTile,
  routeToShoot,
  tileDistance,
  tileOf,
  withinReach,
  headingOf,
  DAY_PHASES,
  dayFraction,
  daylight,
  phaseNow,
  type DayPhase,
  type ContainerIdValue,
  type ItemStack,
  type TilePos,
  type WorldPos,
  runAllowed,
  CollisionFlag,
  GroundKind,
  stepCue,
} from '@inverse/common';
import {
  EntityKind as WireEntityKind,
  EntityPose,
  TargetKind,
  type BonusesPacket,
  type EntityLook,
  type GroundItemWire,
  type Chart,
  type ChartArea,
  type RegionPlaceWire,
  type WorldPlanPacket,
  type InputFrameWire,
  type AdminFactsPacket,
  type TriggersPacket,
  type DropTablePacket,
  type GatheringPacket,
  type DialoguePacket,
  type ProgressPacket,
  type RecipeWire,
  type WorldCheckPacket,
  type ShopStockPacket,
  type SpawnsPacket,
  type ZonesPacket,
  type AdminStatePacket,
  type DefinitionsPacket,
  type OpenMenuPacket,
  type InteractPacket,
  type ShopStatePacket,
  CastTarget,
  type SpellWire,
  type ModelWire,
} from '@inverse/protocol';
import { CameraController, type GameSettings } from './camera';
import { sounds, type SoundPlace } from './sound';
import { loadModel, usePoses } from './avatars';
import { STOREY_HEIGHT } from './scene';
import { InputController } from './input';
import {
  Definitions,
  MEANING_COLOURS,
  meaningOf,
  onlyLookedAt,
  type ContextMenu,
  type MenuEntry,
  type MenuTarget,
} from './menu';
import { Scene, setBuiltShapes, waterBodies } from './scene';
import { PIECE_TILES } from './pieces';
import { type LocalRegion, WorldState } from './world-state';
import type { Connection } from '../net/connection';

/** Input frames per outgoing packet. Two frames means ten packets a second. */
const FRAMES_PER_PACKET = 2;

/**
 * The soonest a follower may work out a fresh route.
 *
 * Following is walking to the tile beside somebody who keeps moving, so the route
 * is redone whenever they step off the tile it was aimed at. This is the floor on
 * how often that can happen, which keeps a search off every frame.
 */
const FOLLOW_REPATH_MS = 150;

/**
 * How much of the leader's path a follower remembers.
 *
 * Only the last few tiles matter: the point is the ground just behind them, not
 * a record of where they have been all afternoon.
 */
const FOLLOW_TRAIL = 8;

/**
 * How long a fight goes without a blow before this client stops keeping up with it.
 *
 * A fight ends in several ways and only one of them, a death, is announced. This
 * is the rest: something wandered off, somebody else killed it, the server lost
 * interest. Long enough to cover the slowest weapon and a chase across a field.
 */
const FIGHT_FORGOTTEN_MS = 6000;

/** Whatever a click in the editor landed on, and exactly where on the ground it was. */
export interface EditorHit {
  tx: number;
  tz: number;
  /** The point on the ground itself, which is how a wall knows which side to take. */
  x: number;
  z: number;
  objectId: number | null;
  /** Which way the thing that was clicked is turned, which a wall needs to be named. */
  objectRotation: number;
  /** And what was done to it: its size, and the height it was built to. */
  objectScale?: number;
  objectLift?: number;
  npcId: number | null;
  /** Ctrl was down: add to what is held rather than replacing it. */
  adding: boolean;
  /** Shift was down: take this out of what is held. */
  removing: boolean;
}

export interface GameCallbacks {
  onSelect(name: string | null): void;
  onRunEnergy(energy: number, running: boolean): void;
  onOpenChat(): void;
  onGameSettings(settings: GameSettings): void;
  /** Null closes any open menu. */
  onContextMenu(menu: ContextMenu | null): void;
  /** A line for the chat log that came from the world rather than a player. */
  onSystemMessage(text: string): void;
  /** What is being held to use on something, so the interface can show it. */
  onUsing(held: { slot: number; name: string } | null): void;
  /** A spell pressed and waiting for what it is to be cast at, or nothing. */
  onCasting(spell: SpellWire | null): void;
  /** A crafting station opened or shut, which the server decides and this shows. */
  onCrafting(
    station: { objectId: number; name: string; tx: number; tz: number; plane: number } | null,
  ): void;
  /** Opens the chat box addressed to one player. */
  onWhisperTo(name: string): void;
  /** Opens the book at one item: what makes it, and what it makes. */
  onLookUp(itemId: number): void;
  /** What is left of the pool a spell is cast from, and the whole of it. */
  onMana(mana: number, max: number): void;
  /** Asking somebody to trade, from the menu over their head. */
  onAskTrade(name: string): void;
  /** Asks somebody into a party. */
  onAskParty(name: string): void;
  /** Opens the report form for somebody. */
  onReport(name: string): void;
  /**
   * Asking somebody to be a friend, from the menu over their head.
   *
   * The world decides what happens; this only says who was pointed at.
   */
  onAskFriend(name: string): void;
  /**
   * How this player already stands with somebody, so the menu can say the right
   * thing or say nothing.
   *
   * Offering to add somebody who is already a friend is an offer that does
   * nothing, and offering to ask again somebody who has already been asked is
   * how a person ends up sending five requests.
   */
  knownAs(name: string): 'friend' | 'asked' | 'asking' | 'none';
  /** A container's contents changed. The interface redraws from this. */
  onContainer(container: ContainerIdValue, slots: readonly (ItemStack | null)[]): void;
  /** Summed bonuses from what the player is wearing. */
  onBonuses(bonuses: BonusesPacket): void;
  /** Levels and experience per skill, indexed by the Skill enum. */
  onSkills(levels: readonly number[], experience: readonly number[]): void;
  /**
   * A skill was just trained, and by how much.
   *
   * Separate from the levels above because the two answer different questions:
   * that is where somebody is up to, and this is what just happened.
   */
  onTrained(held: { skill: number; gained: number; level: number; levelled: boolean }): void;
  /** The bank opened or closed. */
  onBank(open: boolean): void;
  /** A shop opened, closed, or restocked. */
  onShop(packet: ShopStatePacket): void;
  /** What this player is allowed to do, and who is online to do it to. */
  onAdmin(packet: AdminStatePacket): void;
  onAdminFacts(packet: AdminFactsPacket): void;
  onTriggers(packet: TriggersPacket): void;
  onDropTable(packet: DropTablePacket): void;
  onGathering(packet: GatheringPacket): void;
  onWorldCheck(packet: WorldCheckPacket): void;
  /** A moment of black, so the world can move something out of sight. */
  onBlink(): void;
  onDialogue(packet: DialoguePacket): void;
  onProgress(packet: ProgressPacket): void;
  onZones(packet: ZonesPacket): void;
  onSpawns(packet: SpawnsPacket): void;
  onShopStock(packet: ShopStockPacket): void;
  /** The view has been taken far from the character, which is worth acting on. */
  onViewTravelled(tx: number, tz: number): void;
  /** Whether the view is on the character or somewhere else. */
  onViewChanged(panned: boolean): void;
  /** Everything the world contains, once the server has said what that is. */
  onDefinitions(packet: DefinitionsPacket): void;
  /** What the character being played looks like, at login and whenever it changes. */
  onLook(look: EntityLook): void;
  /** How much of a beating the character can still take. */
  onHealth(current: number, max: number): void;
  /** A window a character opened, which the world is waiting on an answer to. */
  onMenu(packet: OpenMenuPacket): void;
}

/** One dot on a map: somebody, something alive, or something lying about. */
export interface MapMark {
  x: number;
  z: number;
  kind: 'player' | 'person' | 'enemy' | 'item';
  name: string;
}

/** What a map drawn from above is given to draw. */
export interface MapView {
  region: LocalRegion;
  /** A picture of every place there is, keyed by id. */
  charts: Record<string, Chart>;
  where: { x: number; z: number; plane: number };
  /** Which way the camera is pointing, so the map can turn with it. */
  yaw: number;
  marks: MapMark[];
  /** Where the current walk ends, when there is one. */
  goal: { x: number; z: number } | null;
  /**
   * What the ground under them is called.
   *
   * The smallest named place they are standing in, since a district inside a
   * town is the more useful answer of the two. Empty where nobody has named the
   * ground, and the region's own name stands in.
   */
  called: string;
  /**
   * Where every region sits, including this one.
   *
   * The world is meant to be continuous, so a map of it is a map of all of
   * them. Empty until the server has said, which is a moment after logging in.
   */
  plan: readonly RegionPlaceWire[];
  /** Which of them is being stood in, by id. */
  planHere: string;
  /**
   * What stretch of the day it is, or nothing before the world has said.
   *
   * On the map view because that is where somebody already looks to find out
   * where they are, and what time it is is the same kind of question.
   */
  time: DayPhase | null;
}

export class Game {
  readonly state = new WorldState();
  readonly scene: Scene;
  readonly camera: CameraController;
  readonly input: InputController;

  private running = false;
  /** Watches the canvas, since the world is a panel and can change size on its own. */
  private watchingSize: ResizeObserver | null = null;
  private frameHandle = 0;
  private lastFrame = 0;
  private accumulator = 0;
  private outgoing: InputFrameWire[] = [];

  readonly definitions = new Definitions();

  /**
   * What the server says each container holds.
   *
   * A copy for drawing, never a source of truth. Dragging updates it immediately so
   * the item follows the cursor, and the server's answer overwrites it either way.
   */
  private readonly containers = new Map<ContainerIdValue, (ItemStack | null)[]>();

  /**
   * What one container holds, for anything that needs to ask rather than draw.
   *
   * The interface draws from its own copy in React; this is the same numbers for
   * whatever is not the interface, which today is the browser tests.
   */
  holding(container: ContainerIdValue): readonly (ItemStack | null)[] {
    return this.containers.get(container) ?? [];
  }

  /**
   * What the editor wants done with clicks and drags on the world.
   *
   * Held here because input arrives here first, and routing it back out to ask
   * would mean the world knowing about the editor rather than the editor knowing
   * about the world. Null puts the world back to being played.
   */
  editor: {
    onPick(hit: EditorHit): void;
    onArea(
      from: { tx: number; tz: number },
      to: { tx: number; tz: number },
      held: { adding: boolean; removing: boolean },
    ): void;
    /**
     * A drag with a tool that acts on what is already held.
     *
     * Answering true means the drag was used, and no area is drawn from it: a
     * selection being moved is not a selection being replaced.
     */
    onDrag(from: { tx: number; tz: number }, to: { tx: number; tz: number }): boolean;
    /** Whether a drag should draw out an area rather than turn the camera. */
    dragsArea(): boolean;
    /**
     * What a drag would carry, when it is carrying rather than marking out.
     *
     * Empty means the drag draws a box. Anything else is drawn where it would
     * land, which is the question somebody dragging a thing is actually asking.
     */
    dragsHeld(): { id: number; tx: number; tz: number; rotation: number; scale?: number }[];
    /**
     * Whether a drag traces a shape rather than a rectangle.
     *
     * A lasso is the same gesture with a different answer: what the pointer
     * covered, rather than the box between where it started and where it ended.
     */
    dragsTrail(): boolean;
    /** The tiles a freehand drag covered, once it is let go of. */
    onTrail(
      tiles: readonly { tx: number; tz: number }[],
      held: { adding: boolean; removing: boolean },
    ): void;
    /**
     * Whether a freehand drag works the ground as it goes.
     *
     * A brush that only lands when the pointer is let go is not a brush, it is
     * a very slow rectangle. Answering true means the tiles are handed over as
     * they are drawn over and onTrail is never called for that drag.
     */
    paintsAsDrawn?(): boolean;
    /** Tiles newly drawn over, while the drag is still held. */
    onTrailStep?(tiles: readonly { tx: number; tz: number }[]): void;
    /** The drag that was being painted has ended. */
    onTrailDone?(): void;
    /**
     * What a drag would leave behind, drawn while it is still held.
     *
     * Worked out where the selection lives rather than here: turning a group
     * moves things about within it, and that arithmetic already exists once.
     * Null means the plain offset, which is what moving is.
     */
    dragPreview(
      from: { tx: number; tz: number },
      to: { tx: number; tz: number },
    ): { id: number; tx: number; tz: number; rotation: number; scale?: number }[] | null;
    /**
     * Whether the tool in hand acts on things rather than on ground.
     *
     * Such a tool outlines whatever is under the pointer, so a wall is picked by
     * pointing at the wall instead of by pointing at the tile it stands on and
     * hoping.
     */
    outlines(): boolean;
    /** What would land on a tile, for showing it there before it does. */
    ghostAt(
      tx: number,
      tz: number,
    ): { id: number; tx: number; tz: number; rotation: number; scale?: number }[];
    /** What the pointer would do here, for the banner at the top of the screen. */
    describe(objectName: string | null, tx: number, tz: number): string;
    /** How wide a stroke here would be, so the outline says where it lands. */
    brushWidth(): number;
    /** Whether a click here would take something away. */
    erasing(): boolean;
  } | null = null;

  /** The tile the roofs were last worked out for, and the rule they followed. */
  private roofTile = { tx: 0, tz: 0 };
  private roofMode: GameSettings['roofs'] = 'underneath';
  private liftedRoofs = false;

  /** Where the pointer last was, in tiles. Read by the development tools. */
  private lastHoveredTile: { tx: number; tz: number } | null = null;

  /** Whether the bank or a shop is open, which changes what clicking an item does. */
  bankOpen = false;
  shopOpen = false;

  /** Piles the server has told this client about, by pile id. */
  private readonly groundItems = new Map<number, GroundItemWire>();

  /**
   * Every pile of one thing lying about, for whatever wants to point at one.
   *
   * A log already on the ground is nearer than the tree it came off more often
   * than it sounds, and a quest that wants three of them should say so.
   */
  pilesOf(itemId: number): { tx: number; tz: number; plane: number }[] {
    const found: { tx: number; tz: number; plane: number }[] = [];

    for (const pile of this.groundItems.values()) {
      if (pile.itemId === itemId) found.push({ tx: pile.tx, tz: pile.tz, plane: pile.plane });
    }

    return found;
  }

  /**
   * A shot the world asked for, and when it gives the camera back.
   *
   * Nothing stops while it is held: this is a camera that has stopped
   * following, not a world that has stopped. Moving ends it early.
   */
  private heldShot: { until: number } | null = null;

  /** The last tile asked about, and what floats on it. Kept for the pointer. */
  private floatingAt: {
    tx: number;
    tz: number;
    found: { id: number; rotation: number } | null;
  } | null = null;

  private settings: GameSettings;
  /** Entity being followed, so deliberate movement can call it off. */
  private following: number | null = null;
  private followRepathAt = 0;

  /** What is being fought, so a chase after it is predicted rather than watched. */
  private fighting: number | null = null;
  /** The tile the current chase was aimed at, so the route is only redone when it moves. */
  private fightTile: TilePos | null = null;
  /** When a blow last passed between the two, which is how a finished fight is noticed. */
  private lastBlow = 0;
  /**
   * How far a blow reaches with what is held, as the server worked it out.
   *
   * One for anything swung, more for a bow or a staff. Everything about walking
   * into a fight turns on it: somebody with a bow stops where the shot comes on,
   * not beside the target.
   */
  private reach = 1;
  /** The tile the current follow route was aimed at, so it is only redone when they move. */
  private followingTile: TilePos | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly connection: Connection,
    private readonly callbacks: GameCallbacks,
    settings: GameSettings,
  ) {
    this.settings = settings;
    this.scene = new Scene(canvas);
    this.scene.applySettings(settings);
    hearWith(settings);
    this.camera = new CameraController(this.scene.camera);
    this.camera.apply(settings);

    this.input = new InputController(
      canvas,
      this.camera,
      {
        onClick: (x, y) => this.handleClick(x, y),
        onContextMenu: (x, y) => this.handleContextMenu(x, y),
        onHover: (x, y) => this.handleHover(x, y),
        onAreaStart: (x, y) => this.beginArea(x, y),
        onAreaMove: (x, y) => this.dragArea(x, y),
        onAreaEnd: (x, y) => this.endArea(x, y),
        onToggleRun: () => this.toggleRun(),
        onOpenChat: () => this.callbacks.onOpenChat(),
      },
      settings,
    );

    this.bindConnection();
  }

  applySettings(settings: GameSettings): void {
    this.settings = settings;
    hearWith(settings);
    this.camera.apply(settings);
    this.input.apply(settings);
    this.scene.applySettings(settings);
  }

  /**
   * The part of the screen the world still has to itself.
   *
   * The player is centred in this rather than in the window, so a panel covers
   * scenery instead of the character. Null means the whole window.
   */
  setPlayableArea(frame: { x: number; y: number; width: number; height: number } | null): void {
    this.camera.setFrame(frame);
  }

  setTyping(typing: boolean): void {
    this.input.setTyping(typing);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastFrame = performance.now();
    this.accumulator = 0;
    this.frameHandle = requestAnimationFrame((time) => this.frame(time));
    window.addEventListener('resize', this.onResize);

    // The world is a panel, so it can change size without the window changing
    // at all: docking something to the side of it makes it narrower.
    if (typeof ResizeObserver !== 'undefined') {
      this.watchingSize = new ResizeObserver(() => this.onResize());
      this.watchingSize.observe(this.scene.canvas);
    }

    // Development only, so live state can be inspected from the console and by the
    // browser tests. Never present in a production build.
    if (import.meta.env.DEV) {
      (window as unknown as { inverse?: Game }).inverse = this;
    }

    // Started now rather than when somebody walks into view, so the first person
    // drawn is a person rather than a shape that becomes one a moment later.
    void loadModel('person');
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frameHandle);
    window.removeEventListener('resize', this.onResize);
    this.watchingSize?.disconnect();
    this.watchingSize = null;
    this.input.dispose();
    this.scene.dispose();
  }

  private readonly onResize = (): void => {
    this.scene.resize();
    this.camera.resized();
  };

  // -------------------------------------------------------------------------
  // Loops
  // -------------------------------------------------------------------------

  /**
   * What the world's clock says, as an amount of daylight.
   *
   * Run here rather than asked for, because a day is an hour long and a frame
   * is a moment: the server says how long a day is and when it last looked, and
   * the client can carry that on its own. A held time of day is the designer's,
   * and beats the clock.
   */
  private lightNow(): number {
    const held = this.clock;
    if (!held) return 1;

    if (held.forced.length > 0) return daylightForPhase(held.forced);

    return daylight(dayFraction(Date.now() + held.drift, held.dayMs));
  }

  /** What the world last said about its own clock. */
  private clock: { dayMs: number; drift: number; forced: string } | null = null;

  /** The stretch of day it is, for anything that wants to say so in words. */
  timeOfDay(): { phase: DayPhase; held: boolean } | null {
    const clock = this.clock;
    if (!clock) return null;

    if (clock.forced.length > 0 && DAY_PHASES.includes(clock.forced as DayPhase)) {
      return { phase: clock.forced as DayPhase, held: true };
    }

    return { phase: phaseNow(Date.now() + clock.drift, clock.dayMs), held: false };
  }

  private frame(time: number): void {
    if (!this.running) return;
    this.frameHandle = requestAnimationFrame((next) => this.frame(next));

    /*
     * However long the frame took, up to a second.
     *
     * The bound is for a tab coming back from the background, which should not
     * replay a minute of steps. It used to be a quarter of a second, which also
     * threw away time on any frame slower than four a second: the prediction then
     * walked slower than the server did, fell behind, and was dragged forward
     * by the corrections. The server walks in real time, so this has to as well.
     */
    const deltaMs = Math.min(time - this.lastFrame, 1000);
    this.lastFrame = time;

    // Fixed step movement, however long the frame took.
    this.accumulator += deltaMs;
    while (this.accumulator >= MOVE_STEP_MS) {
      this.accumulator -= MOVE_STEP_MS;
      this.movementStep();
    }

    // Keys that turn the camera are held rather than pressed, so they are applied
    // per frame like movement rather than once per press.
    const turn = this.input.cameraTurn();
    this.camera.turn(turn.yaw, turn.pitch, deltaMs / 1000);

    // The sky, from the world's clock. Cheap enough to do every frame and
    // wrong to do any other way: the two turns of the day are meant to be seen
    // happening rather than to arrive between one packet and the next.
    this.scene.setDaylight(this.lightNow());

    this.state.decaySmoothing(deltaMs);
    this.state.interpolateAll(time);

    // How far this frame sits between the last movement step and the next one. Other
    // entities are already interpolated; without this the local player would be the
    // only thing on screen moving in 50ms jumps.
    const alpha = this.accumulator / MOVE_STEP_MS;
    this.scene.syncEntities(this.state, alpha);

    // Under a roof, the roofs come off. Checked as the tile changes rather than
    // every frame, since neither the roof nor the tile moves in between.
    const standing = this.localTileCoords();
    // Roofs come off under one, near the pointer, both, or always, depending on
    // what somebody asked for.
    const mode = this.settings.roofs;
    const under = mode === 'underneath' || mode === 'both';

    if (
      standing.tx !== this.roofTile.tx ||
      standing.tz !== this.roofTile.tz ||
      this.roofMode !== mode
    ) {
      this.roofTile = standing;
      this.roofMode = mode;

      this.scene.showRoofs(
        mode === 'always' ? false : !(under && this.scene.roofOver(standing.tx, standing.tz)),
      );
    }

    if (mode === 'hovered' || mode === 'both') {
      this.scene.liftRoofsAround(this.lastHoveredTile);
    } else if (this.liftedRoofs) {
      this.scene.liftRoofsAround(null);
    }

    this.liftedRoofs = mode === 'hovered' || mode === 'both';

    // Anything the player is standing in rather than on is not drawn for them,
    // the same courtesy the world does for somebody standing under you.
    this.scene.hideSceneryUnderfoot(this.localTileCoords(), this.standingIn());

    this.scene.updateTileOverlay(
      this.localTileCoords(),
      this.settings.showGrid,
      this.settings.highlightTile,
      // Around what is being looked at once the view has left the character,
      // since that is where the work is.
      this.viewingTile() ?? undefined,
      // The same question movement asks, so the overlay cannot disagree with
      // the world it is drawn over. Only while editing: this is a tool.
      this.settings.showBlocked && this.editor !== null
        ? (tx, tz) => this.state.collision?.isBlocked(tx, tz, this.state.local.pos.plane) ?? false
        : undefined,
    );

    // A shot the world asked for ends on its own, and ends early the moment the
    // player does anything: nobody should have to sit through a scene twice.
    if (this.heldShot && performance.now() >= this.heldShot.until) {
      this.heldShot = null;
      this.followCharacter();
    }

    const focus = this.viewFocus ?? this.state.renderPosition(alpha);
    // Whatever is being stood on, so going upstairs takes the camera up with it
    // rather than leaving it framed on the ground below, and walking out along a
    // dock keeps it level with the boards. It used to take the ground, which
    // under a dock is the seabed: the view sank as the pier went out.
    const floorHeight = this.scene.surfaceAt(focus.x, focus.z) + focus.plane * STOREY_HEIGHT;
    this.camera.update(focus, floorHeight, deltaMs / 1000, (x, z) => this.scene.surfaceAt(x, z));
    this.listen(focus, time);
    // The scene draws its water and its ground in a band round whoever is
    // reading it, so it has to be told where they are looking rather than where
    // they are standing: in the tools those are different places.
    this.scene.lookAt(Math.floor(focus.x), Math.floor(focus.z));

    // And the server sends ground round the same point, so a camera sent across
    // the continent arrives at ground rather than at an empty grid.
    this.tellWhereWatching(focus);
    this.scene.render();
  }

  /**
   * Hands whatever is under a point to the editor.
   *
   * A character standing on a tile is what was meant by clicking it, so it takes
   * precedence over the scenery, which in turn takes precedence over the ground.
   */
  private pickForEditor(screenX: number, screenY: number): boolean {
    const editor = this.editor;
    if (!editor) return false;

    const hit = this.scene.pickEntities(screenX, screenY)[0];
    const entity = hit ? this.state.entities.get(hit.id) : undefined;
    const npcId = hit && entity?.kind === WireEntityKind.Npc ? hit.id : null;

    const ground = this.scene.pickGround(screenX, screenY);

    // A character drawn against the sky has no ground behind it, and clicking one
    // there still means that character.
    if (!ground) {
      if (npcId === null || !entity) return false;

      editor.onPick({
        tx: Math.floor(entity.x),
        tz: Math.floor(entity.z),
        x: entity.x,
        z: entity.z,
        objectId: null,
        objectRotation: 0,
        npcId,
        adding: this.input.addingToSelection,
        removing: this.input.removingFromSelection,
      });
      return true;
    }

    const object = this.scene.pickObject(screenX, screenY);

    /*
     * Whichever the pointer actually landed on.
     *
     * It used to be the object only when it stood on the same tile as the
     * ground behind it, which is true of a chest and false of everything with
     * any height: clicking a tree hits its trunk halfway up, and the ground ray
     * carries on past it and lands two tiles behind. So the tile behind the tree
     * was selected, and the only way to select the tree was to find the ground
     * it was standing on and click that instead.
     *
     * The near one wins. An object in front of the ground is what was clicked,
     * and its own tile is the tile that was meant.
     */
    const took = object !== null && object.distance <= ground.distance + 0.001;
    const onSameTile = object && object.tx === ground.tx && object.tz === ground.tz;
    const held = took || onSameTile ? object : null;
    const at = held && took ? { tx: held.tx, tz: held.tz } : { tx: ground.tx, tz: ground.tz };

    editor.onPick({
      tx: at.tx,
      tz: at.tz,
      x: took && held ? held.tx + 0.5 : ground.x,
      z: took && held ? held.tz + 0.5 : ground.z,
      objectId: held ? held.id : null,
      objectRotation: held ? held.rotation : 0,
      objectScale: held ? held.scale : undefined,
      objectLift: held ? held.lift : undefined,
      npcId: npcId ?? null,
      adding: this.input.addingToSelection,
      removing: this.input.removingFromSelection,
    });
    return true;
  }

  /** Takes your own character off screen, which is what editing wants. */
  showLocalCharacter(showing: boolean): void {
    this.scene.showLocalCharacter(showing);
  }

  /**
   * Leaves the view where it is, off the character.
   *
   * Called as the editor opens: the camera stops being the character's and
   * becomes the editor's, starting from wherever they were standing.
   */
  detachView(): void {
    if (this.viewFocus) return;

    const at = this.state.renderPosition();
    this.viewFocus = { x: at.x, z: at.z, plane: at.plane };
    this.callbacks.onViewChanged(true);
  }

  /** Hides everything standing on the ground, so the ground can be worked on. */
  showScenery(showing: boolean): void {
    this.scene.showScenery(showing);
  }

  /**
   * The tile under a point on screen, for anything that is not a click.
   *
   * Dropping something dragged in from the browser has no mouse button behind
   * it, so it cannot go through the picking above; what it needs is only the
   * ground, and only where.
   */
  /**
   * How high something laid on a tile would sit: the ground, or the water over it.
   *
   * What the dock tool takes its height from, so a dock built off a beach starts
   * level with the beach and carries that height out over the water.
   */
  buildHeightAt(tx: number, tz: number): number {
    const x = tx + 0.5;
    const z = tz + 0.5;
    const bed = this.scene.heightAt(x, z);
    const afloat = this.scene.waterLevelAt(x, z);

    return afloat !== null && afloat > bed ? afloat : bed;
  }

  tileAt(
    screenX: number,
    screenY: number,
  ): { tx: number; tz: number; x: number; z: number } | null {
    return this.scene.pickGround(screenX, screenY);
  }

  /** Whether the add modifier is down, which the editor asks before a drag starts. */
  holdingAdd(): boolean {
    return this.input.addingToSelection;
  }

  /** Which selection modifiers are down: Ctrl adds to what is held, Shift takes away. */
  private heldModifiers(): { adding: boolean; removing: boolean } {
    return {
      adding: this.input.addingToSelection,
      removing: this.input.removingFromSelection,
    };
  }

  /** Where an area drag started, in tiles, while one is being drawn. */
  private areaFrom: { tx: number; tz: number } | null = null;

  /** Begins an area drag, if the editor wants one. Called from input. */
  beginArea(screenX: number, screenY: number): boolean {
    // A spot being picked for the tools is a click, never the corner of an area.
    if (this.tilePick) return false;
    if (!this.editor?.dragsArea()) return false;

    const ground = this.scene.pickGround(screenX, screenY);
    if (!ground) return false;

    this.areaFrom = { tx: ground.tx, tz: ground.tz };

    // Carrying something shows the thing, not a box round where the pointer has
    // been. The cursor says so as well.
    this.carrying = this.editor.dragsHeld();
    if (this.carrying.length > 0) {
      this.canvas.classList.add('grabbing');
      this.scene.showGhost(this.carrying);
      return true;
    }

    if (this.editor.dragsTrail()) {
      this.trail = [{ tx: ground.tx, tz: ground.tz }];
      this.scene.showTrail(this.trail);

      // A brush lands where it is put down, before it has been moved anywhere.
      if (this.editor.paintsAsDrawn?.()) this.editor.onTrailStep?.(this.trail);
      return true;
    }

    this.scene.showArea(this.areaFrom, this.areaFrom);
    return true;
  }

  /** Every tile a freehand drag has been over, in the order it went. */
  private trail: { tx: number; tz: number }[] = [];

  /** What a drag is carrying, drawn where it would land while it is held. */
  private carrying: { id: number; tx: number; tz: number; rotation: number; scale?: number }[] = [];

  /** Grows the area being drawn, or the trail being traced. */
  dragArea(screenX: number, screenY: number): void {
    if (!this.areaFrom) return;

    const ground = this.scene.pickGround(screenX, screenY);
    if (!ground) return;

    if (this.carrying.length > 0) {
      const previewed = this.editor?.dragPreview(this.areaFrom, ground) ?? null;
      if (previewed) {
        this.scene.showGhost(previewed);
        return;
      }

      const dx = ground.tx - this.areaFrom.tx;
      const dz = ground.tz - this.areaFrom.tz;

      this.scene.showGhost(
        this.carrying.map((item) => ({ ...item, tx: item.tx + dx, tz: item.tz + dz })),
      );
      return;
    }

    if (this.trail.length > 0) {
      const last = this.trail[this.trail.length - 1];
      if (last && last.tx === ground.tx && last.tz === ground.tz) return;

      // The pointer skips tiles when it moves quickly, and a lasso with holes in
      // it is a lasso that misses things. Whatever was stepped over is filled in.
      const before = this.trail.length;
      if (last) for (const between of tilesBetween(last, ground)) this.addToTrail(between);
      this.addToTrail({ tx: ground.tx, tz: ground.tz });

      this.scene.showTrail(this.trail);

      // Only what is new, so a long stroke does not resend everything it has
      // covered on every tile it crosses.
      if (this.editor?.paintsAsDrawn?.() && this.trail.length > before) {
        this.editor.onTrailStep?.(this.trail.slice(before));
      }

      return;
    }

    this.scene.showArea(this.areaFrom, ground);
  }

  private addToTrail(tile: { tx: number; tz: number }): void {
    if (this.trail.length >= 4096) return;
    if (this.trail.some((held) => held.tx === tile.tx && held.tz === tile.tz)) return;
    this.trail.push(tile);
  }

  /**
   * Finishes an area drag and hands it to the editor.
   *
   * A press and a release on the same tile is a click, whatever mode the editor is
   * in, so it picks rather than drawing out an area of one tile.
   */
  endArea(screenX: number, screenY: number): void {
    const from = this.areaFrom;
    this.areaFrom = null;

    this.carrying = [];
    this.scene.hideGhost();
    this.canvas.classList.remove('grabbing');

    if (!from) return;

    const ground = this.scene.pickGround(screenX, screenY);
    const to = ground ? { tx: ground.tx, tz: ground.tz } : from;

    if (this.trail.length > 0) {
      const drawn = this.trail;
      const painted = this.editor?.paintsAsDrawn?.() ?? false;
      this.trail = [];
      this.scene.hideTrail();

      // Already done, tile by tile, on the way round.
      if (painted) {
        this.editor?.onTrailDone?.();
        return;
      }

      if (drawn.length === 1) {
        this.pickForEditor(screenX, screenY);
        return;
      }

      this.editor?.onTrail(drawn, this.heldModifiers());
      return;
    }

    if (to.tx === from.tx && to.tz === from.tz) {
      this.scene.hideArea();
      // Nothing was dragged anywhere, so this was a click, whatever the tool was
      // carrying. Move picks what is under the pointer the way Select does, which
      // is what stops somebody having to put the tool down to choose the next
      // thing to move.
      this.pickForEditor(screenX, screenY);
      return;
    }

    // A tool that works on the selection gets first refusal on the drag.
    if (this.editor?.onDrag(from, to)) {
      this.scene.hideArea();
      return;
    }

    this.scene.showArea(from, to);
    this.editor?.onArea(from, to, this.heldModifiers());
  }

  /**
   * Where the camera is looking, when that is somewhere other than the character.
   *
   * The editor needs to work on parts of the map nobody is standing in, and
   * walking there is not editing. Null puts the view back on the character.
   */
  private viewFocus: { x: number; z: number; plane: number } | null = null;

  /** The last place the server was told the camera was, so it is not told twice. */
  private toldWatching: { tx: number; tz: number } | null = null;

  /**
   * Tells the server where the camera is, when that has changed enough to matter.
   *
   * What it decides is which squares of ground to send, and a square is sixty
   * four tiles, so there is nothing to say until the camera has moved about that
   * far. Letting go of the camera says so once, and the world goes back to
   * following the character.
   */
  private tellWhereWatching(focus: { x: number; z: number }): void {
    if (!this.viewFocus) {
      if (!this.toldWatching) return;
      this.toldWatching = null;
      this.connection.sendWatching({ away: false, tx: 0, tz: 0 });
      return;
    }

    const at = { tx: Math.floor(focus.x), tz: Math.floor(focus.z) };
    if (
      this.toldWatching &&
      Math.abs(at.tx - this.toldWatching.tx) < 24 &&
      Math.abs(at.tz - this.toldWatching.tz) < 24
    ) {
      return;
    }

    this.toldWatching = at;
    this.connection.sendWatching({ away: true, ...at });
  }

  /** How fast the view travels while the keys are held, in tiles a second. */
  private static readonly PAN_SPEED = 14;

  /** Whether the character is walking after somebody, for anything that asks. */
  get isFollowing(): boolean {
    return this.following !== null;
  }

  /** Whether the view has been taken off the character. */
  get panned(): boolean {
    return this.viewFocus !== null;
  }

  /**
   * Moves the view along a direction in world space.
   *
   * The keys are turned into a direction by the input, which has already swung
   * them round to face the way the camera does: forward is into the screen
   * whichever way the camera has been turned. Swinging them round a second time
   * here is what made the view slide off at an angle of its own, so this takes
   * the direction as given.
   *
   * Beyond a certain distance the character is brought along. Everything alive is
   * sent to a client because somebody is standing near it, so a view that
   * wanders too far ends up looking at a world with nothing in it.
   */
  panBy(dx: number, dz: number): void {
    const from = this.viewFocus ?? { ...this.state.renderPosition() };
    const step = (Game.PAN_SPEED * MOVE_STEP_MS) / 1000;

    const x = from.x + dx * step;
    const z = from.z + dz * step;

    if (!this.viewFocus) this.callbacks.onViewChanged(true);
    this.viewFocus = { x, z, plane: from.plane };

    const here = this.state.local.pos;
    if (Math.hypot(x - here.x, z - here.z) > 24) {
      this.callbacks.onViewTravelled(Math.floor(x), Math.floor(z));
    }
  }

  /**
   * Everything a map drawn from above needs, gathered in one pass.
   *
   * Asked for by the minimap on every frame it draws, so it is a read of what
   * is already held rather than any work: the region the client was sent, where
   * the character is standing, which way the camera is pointing, and a dot for
   * everything worth a dot. Nothing here changes anything.
   */
  /** The arrangement of the world, as the server last said it. */
  private plan: readonly RegionPlaceWire[] = [];
  /**
   * A picture of every place there is, keyed by id.
   *
   * The map of everywhere draws each place from its own chart, so the world
   * does not have to be walked before it can be looked at.
   */
  private charts: Record<string, Chart> = {};
  private planHere = '';

  /** Kept when it arrives, which is once, shortly after logging in. */
  setWorldPlan(packet: WorldPlanPacket): void {
    this.plan = packet.regions;
    this.planHere = packet.here;
    this.charts = packet.charts;
  }

  /** The water bodies in a stretch of ground, so a check can time the search. */
  waterBodiesForTest(
    region: LocalRegion,
    from: { tx: number; tz: number; width: number; height: number },
  ): unknown[] {
    return waterBodies(region, from);
  }

  /**
   * Whether the world has caught up with somebody who has just arrived in it.
   *
   * Logging in, stepping through a door, or being put down on the far side of a
   * continent all end the same way: the ground is asked for, and it arrives a
   * square at a time over the next second or so. Until the square underfoot has
   * both arrived and been built, there is nothing to look at, so it is better to
   * say so than to show somebody a hole in the sea.
   *
   * The count is of the nine squares around them, which is what makes the wait
   * read as progress rather than as a hang.
   */
  arriving(): { ready: boolean; have: number; want: number } | null {
    const region = this.state.region;
    if (!region) return { ready: false, have: 0, want: 9 };

    // Nothing to say while the view is off somewhere else. The ground is being
    // streamed round the camera then, not round the character, so the squares
    // under the character are exactly the ones that have been let go of: the
    // notice would come up the moment the camera left and stay up until it came
    // back, which is not what arriving somewhere means.
    if (this.viewFocus !== null) return { ready: true, have: 1, want: 1 };

    const here = this.state.renderPosition();
    const tx = Math.floor(here.x) - region.originX;
    const tz = Math.floor(here.z) - region.originZ;

    let have = 0;
    let want = 0;

    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const x = tx + dx * PIECE_TILES;
        const z = tz + dz * PIECE_TILES;
        if (x < 0 || z < 0 || x >= region.width || z >= region.height) continue;

        want += 1;
        if (this.state.hasGround(x, z)) have += 1;
      }
    }

    // The one they are standing on has to be drawn, not merely sent: everything
    // else can arrive while they look around.
    const under = this.state.hasGround(tx, tz) && this.scene.hasTerrain(tx, tz);
    return { ready: under && have >= Math.min(want, 4), have, want: Math.max(1, want) };
  }

  mapView(): MapView | null {
    const region = this.state.region;
    if (!region) return null;

    const here = this.state.renderPosition();
    const marks: MapMark[] = [];

    for (const entity of this.state.entities.values()) {
      if (entity.id === this.state.localId) continue;
      if (entity.plane !== here.plane) continue;

      marks.push({
        x: entity.x,
        z: entity.z,
        kind:
          entity.kind === WireEntityKind.Player
            ? 'player'
            : entity.combatLevel > 0
              ? 'enemy'
              : 'person',
        name: entity.name,
      });
    }

    for (const pile of this.groundItems.values()) {
      if (pile.plane !== here.plane) continue;
      marks.push({ x: pile.tx + 0.5, z: pile.tz + 0.5, kind: 'item', name: '' });
    }

    const last = this.state.route[this.state.route.length - 1];

    return {
      region,
      where: here,
      yaw: this.camera.yaw,
      marks,
      goal: last ? { x: last.tx + 0.5, z: last.tz + 0.5 } : null,
      called: this.calledHere(region, here),
      plan: this.plan,
      planHere: this.planHere,
      charts: this.charts,
      time: this.timeOfDay()?.phase ?? null,
    };
  }

  /**
   * What the ground somebody is standing on is called.
   *
   * The area first, because the whole continent is divided into areas and towns
   * with borders that follow rivers and ridges rather than boxes, and the area
   * is the answer to where somebody is. A named place is a fallback for ground
   * that has one drawn round it and no area under it, and the smallest of those
   * wins, because a district inside a town is the more particular answer.
   */
  private calledHere(region: LocalRegion, at: { x: number; z: number; plane: number }): string {
    const tx = Math.floor(at.x);
    const tz = Math.floor(at.z);

    const inside = this.areaHere(region, tx, tz);
    if (inside) return inside.name;

    let found = '';
    let smallest = Number.POSITIVE_INFINITY;

    for (const place of region.places) {
      if (place.plane !== at.plane) continue;

      const left = region.originX + place.tx;
      const top = region.originZ + place.tz;
      if (tx < left || tx >= left + place.width) continue;
      if (tz < top || tz >= top + place.height) continue;

      const size = place.width * place.height;
      if (size >= smallest) continue;

      smallest = size;
      found = place.name;
    }

    return found;
  }

  /**
   * The area a world tile falls in, from the ground that has arrived.
   *
   * Read off the tile rather than off the chart, which is one reading every
   * eighteen tiles on a continent and would put a border in the wrong field.
   * Nothing where the square has not arrived, which cannot happen for the tile
   * somebody is standing on.
   */
  private areaHere(region: LocalRegion, tx: number, tz: number): ChartArea | null {
    const x = tx - region.originX;
    const z = tz - region.originZ;
    if (x < 0 || z < 0 || x >= region.width || z >= region.height) return null;

    const held = region.area[z * region.width + x] ?? 0;
    if (held > 0) return region.chart.areas[held - 1] ?? null;

    // The ground under them may not have arrived yet, and somebody being told
    // where they are landing is the whole point of saying anything at all. The
    // chart is one reading every few tiles and is there from the moment the
    // region is, which is near enough for a name.
    const chart = region.chart;
    const bx = Math.floor(x / chart.step);
    const bz = Math.floor(z / chart.step);
    if (bx < 0 || bz < 0 || bx >= chart.width || bz >= chart.height) return null;

    const coarse = chart.area[bz * chart.width + bx] ?? 0;
    return coarse > 0 ? (chart.areas[coarse - 1] ?? null) : null;
  }

  /**
   * The same place, said in full: South Kestral, The Glass Waste.
   *
   * Used where a name has to stand on its own, away from the map that would
   * otherwise say which part of the world it is in.
   */
  whereAmI(): string {
    const region = this.state.region;
    if (!region) return '';

    const here = this.state.renderPosition();
    const area = this.areaHere(region, Math.floor(here.x), Math.floor(here.z));
    if (!area) return this.calledHere(region, here);

    const above = [area.part, area.region].filter(Boolean).join(' ');
    return above ? `${above}, ${area.name}` : area.name;
  }

  /**
   * Walks to a tile, for a map that was clicked rather than the ground.
   *
   * The same road as clicking the world: a map that took a different one would
   * be a second set of rules about what a click means, and the first thing to
   * disagree would be whether walking stops what is being done.
   */
  walkToTile(tx: number, tz: number): void {
    this.walkTo(tx, tz, this.state.local.pos.plane);
  }

  /**
   * The scenery the player is standing in rather than on.
   *
   * Only things that do not stop anybody walking through them, and only things
   * that stand on a tile: boards under your feet are what you are standing on,
   * and taking them away would leave you walking on the sea.
   */
  private standingIn(): number[] {
    const region = this.state.region;
    if (!region) return [];

    const here = this.localTileCoords();
    const plane = this.state.local.pos.plane;
    const found: number[] = [];

    for (const object of region.objects) {
      if (region.originX + object.tx !== here.tx) continue;
      if (region.originZ + object.tz !== here.tz) continue;
      if ((object.plane ?? 0) !== plane) continue;
      if (this.state.barrierOf(object.id) !== 'open') continue;
      if (this.state.layerFor(object.id) !== 'standing') continue;

      found.push(object.id);
    }

    return found;
  }

  /**
   * The next click in the world picks a spot and is handed over, instead of
   * doing what a click does. For the tools, which ask where something goes.
   */
  pickTileOnce(done: (at: { tx: number; tz: number; plane: number } | null) => void): void {
    this.tilePick?.(null);
    this.tilePick = done;
  }

  /** Calls off a pick nobody is going to make. */
  cancelTilePick(): void {
    const done = this.tilePick;
    this.tilePick = null;
    done?.(null);
  }

  private tilePick: ((at: { tx: number; tz: number; plane: number } | null) => void) | null = null;

  /** Points the view at a tile, which is what focusing on a selection does. */
  lookAt(tx: number, tz: number): void {
    if (!this.viewFocus) this.callbacks.onViewChanged(true);
    this.viewFocus = { x: tx + 0.5, z: tz + 0.5, plane: this.state.local.pos.plane };

    const here = this.state.local.pos;
    if (Math.hypot(tx + 0.5 - here.x, tz + 0.5 - here.z) > 24) {
      this.callbacks.onViewTravelled(tx, tz);
    }
  }

  /** The tile the view is looking at, when it is not on the character. */
  viewingTile(): { tx: number; tz: number } | null {
    return this.viewFocus
      ? { tx: Math.floor(this.viewFocus.x), tz: Math.floor(this.viewFocus.z) }
      : null;
  }

  /** Puts the view back on the character. */
  followCharacter(): void {
    if (this.viewFocus) this.callbacks.onViewChanged(false);
    this.viewFocus = null;
    this.heldShot = null;
  }

  /** Draws an area outline on the ground, for a brush or a selection. */
  markArea(from: { tx: number; tz: number }, to: { tx: number; tz: number }): void {
    this.scene.showArea(from, to);
  }

  /** Takes the drawn area off the ground. */
  clearArea(): void {
    this.areaFrom = null;
    this.scene.hideArea();
  }

  private movementStep(): void {
    if (!this.state.ready) return;

    // The keys move the view while the editor holds the world, the way they do in
    // every editor. They move nothing at all while it does not: walking is
    // clicking where to walk, and steering a character with the keys never sat
    // well with a world built around a destination.
    if (this.editor) {
      const held = this.input.movementVector();
      if (held.x !== 0 || held.z !== 0) this.panBy(held.x, held.z);
    }

    // Following somebody is a route like any other, worked out again each time
    // they move. Steering a follower by hand is what left one standing between
    // two tiles every time the person in front stopped.
    if (this.following !== null) this.keepUpWithTarget();
    if (this.fighting !== null) this.keepUpWithFight();

    this.faceTarget();

    // Held where they stand: the server will not move them, so neither does this.
    if (this.bound && this.state.local.path.length > 0) this.state.local.path = [];

    const wasRunning = this.state.local.running;
    const frame = this.state.simulate(this.state.local.running);
    if (!frame) return;

    // Out of energy switches run off in the step itself, so the button follows.
    if (wasRunning && !this.state.local.running) {
      this.callbacks.onRunEnergy(this.state.local.runEnergy, false);
    }

    this.outgoing.push({ sequence: frame.sequence, running: frame.running });

    if (this.outgoing.length >= FRAMES_PER_PACKET) {
      this.connection.sendInput(this.outgoing);
      this.outgoing = [];
    }
  }

  // -------------------------------------------------------------------------
  // Intent
  // -------------------------------------------------------------------------

  /**
   * Everything under the cursor, in the order a menu should list it.
   *
   * A single nearest hit is not enough: a pile lying under another player, or under
   * the player's own model, would be unreachable, and one entity standing in front
   * of another would hide it. So every hit is collected and the menu stacks them.
   *
   * Order is by kind first and distance second. Entities outrank scenery and piles
   * because a left click on somebody standing over an item should deal with the
   * person, and walking is last because it is what every other option is not.
   */
  private targetsAt(
    screenX: number,
    screenY: number,
    /**
     * Whether to include the things the pointer ordinarily goes through.
     *
     * True for the right-click menu, which is where everything that is there
     * has to be reachable, and false everywhere else. Without it, examining a
     * particular pine would be impossible; with it on all the time, a wood
     * would be a wall of things to click on with nothing to do to any of them.
     */
    everything = false,
  ): MenuTarget[] {
    const plane = this.state.local.pos.plane;
    const found: { target: MenuTarget; rank: number; distance: number }[] = [];

    for (const hit of this.scene.pickEntities(screenX, screenY)) {
      const entity = this.state.entities.get(hit.id);
      if (!entity) continue;

      found.push({
        rank: 0,
        distance: hit.distance,
        target:
          entity.kind === WireEntityKind.Player
            ? {
                kind: 'player',
                entityId: entity.id,
                name: entity.name,
                combatLevel: entity.combatLevel,
              }
            : {
                kind: 'npc',
                entityId: entity.id,
                defId: entity.defId,
                name: entity.name,
                combatLevel: entity.combatLevel,
              },
      });
    }

    // Anything there is nothing to do to is not something to click: it is part
    // of the place. The pointer goes straight through it, so a door behind a
    // wall can be opened from outside, and a person standing in a wood can be
    // clicked on through the trees between.
    //
    // Being able to look at something is not something to do to it. A pine
    // planted as scenery offers Examine and nothing else, and a hillside of
    // them was a hillside of things the pointer stopped on. Examining one is
    // still there on the right-click menu, which is where it belongs.
    const objectHit = this.scene.pickObject(screenX, screenY, (id) => {
      const definition = this.definitions.object(id);
      if (!definition) return true;

      /*
       * A wall, a floor, a roof: part of the place rather than a thing in it.
       *
       * The pointer goes through these however it was asked. They are not on
       * the right-click menu either, because there is nothing to put on it: a
       * roof has no use and looking at one tells nobody anything. Leaving them
       * on it meant standing inside a house and right-clicking the roof over
       * your head instead of the chest in front of you, and the roof was not
       * even being drawn at the time.
       */
      if (definition.piece && onlyLookedAt(definition)) return false;

      // Everything else that can only be looked at is reached from the menu and
      // walked through the rest of the time.
      return everything || !onlyLookedAt(definition);
    });

    if (objectHit) {
      found.push({
        rank: 1,
        distance: objectHit.distance,
        target: {
          kind: 'object',
          defId: objectHit.id,
          tx: objectHit.tx,
          tz: objectHit.tz,
          plane,
          index: objectHit.index,
          name: this.definitions.object(objectHit.id)?.name ?? 'Scenery',
        },
      });
    }

    for (const hit of this.scene.pickGroundItems(screenX, screenY)) {
      const pile = this.groundItems.get(hit.id);
      if (!pile) continue;

      found.push({
        rank: 2,
        distance: hit.distance,
        target: {
          kind: 'pile',
          pileId: pile.id,
          itemId: pile.itemId,
          quantity: pile.quantity,
          name: this.definitions.item(pile.itemId)?.name ?? `Item ${pile.itemId}`,
        },
      });
    }

    const groundHit = this.scene.pickGround(screenX, screenY);
    if (groundHit) {
      // Anything afloat answers to the tile it sits on rather than to its own
      // shape. A fishing spot is a ripple that grows and shrinks over a tile of
      // water two metres wide, and hitting the ripple is a game of its own.
      const afloat = this.afloatOn(groundHit.tx, groundHit.tz);
      if (afloat && !found.some((entry) => entry.target.kind === 'object')) {
        found.push({
          rank: 1,
          distance: groundHit.distance,
          target: {
            kind: 'object',
            defId: afloat.id,
            tx: groundHit.tx,
            tz: groundHit.tz,
            plane,
            index: this.scene.instanceOn(afloat.id, groundHit.tx, groundHit.tz) ?? 0,
            name: this.definitions.object(afloat.id)?.name ?? 'Scenery',
          },
        });
      }

      found.push({
        rank: 3,
        distance: groundHit.distance,
        target: { kind: 'ground', tx: groundHit.tx, tz: groundHit.tz, plane },
      });
    }

    found.sort((a, b) => a.rank - b.rank || a.distance - b.distance);
    return found.map((entry) => entry.target);
  }

  /**
   * Whatever floats on one tile, if anything does.
   *
   * The answer is kept for as long as the pointer stays on the same tile, which
   * is nearly always: this is asked on every pointer move and the region can
   * hold thousands of things.
   */
  private afloatOn(tx: number, tz: number): { id: number; rotation: number } | null {
    if (this.floatingAt && this.floatingAt.tx === tx && this.floatingAt.tz === tz) {
      return this.floatingAt.found;
    }

    const region = this.state.region;
    let found: { id: number; rotation: number } | null = null;

    if (region) {
      for (const object of region.objects) {
        if (region.originX + object.tx !== tx || region.originZ + object.tz !== tz) continue;
        if (this.definitions.object(object.id)?.afloat !== true) continue;

        found = { id: object.id, rotation: object.rotation };
        break;
      }
    }

    this.floatingAt = { tx, tz, found };
    return found;
  }

  private handleClick(screenX: number, screenY: number): void {
    this.callbacks.onContextMenu(null);

    // Something in the tools asked where: this click is the answer, and does
    // nothing else.
    if (this.tilePick) {
      const done = this.tilePick;
      this.tilePick = null;
      const ground = this.scene.pickGround(screenX, screenY);
      done(ground ? { tx: ground.tx, tz: ground.tz, plane: this.state.local.pos.plane } : null);
      return;
    }

    // While the editor holds the world, a click picks or places rather than walks.
    if (this.editor && this.pickForEditor(screenX, screenY)) return;

    const targets = this.targetsAt(screenX, screenY);
    const first = targets[0];
    if (!first) return;

    // Something held for use goes on whatever was clicked, instead of whatever
    // clicking it would ordinarily do. So does a spell waiting for a target.
    if (this.useOn(first)) return;
    if (this.castOn(first)) return;

    this.select(first);

    // The first entry of the first target, which is the same thing the menu would
    // list at the top.
    const entry = this.entriesFor(first)[0];
    if (entry) entry.run();
  }

  /** Opens the menu for whatever is at a screen point. */
  openMenuAt(screenX: number, screenY: number): void {
    this.handleContextMenu(screenX, screenY);
  }

  private handleContextMenu(screenX: number, screenY: number): void {
    // Everything under the cursor, including whatever the pointer walks through
    // the rest of the time: the menu is how something with only a name and a
    // description is reached.
    const targets = this.targetsAt(screenX, screenY, true);
    const first = targets[0];
    if (!first) {
      this.callbacks.onContextMenu(null);
      return;
    }

    this.select(first);

    // Every target under the cursor contributes its entries, so nothing that is
    // there can be unreachable because something else is drawn in front of it.
    const entries: MenuEntry[] = [];
    for (const target of targets) entries.push(...this.entriesFor(target));

    this.callbacks.onContextMenu({
      screenX,
      screenY,
      title: this.titleOf(first),
      entries,
    });
  }

  /**
   * Names whatever the pointer is over, and what a left click would do to it.
   *
   * Read before clicking rather than after, which is the point: a player should
   * know they are about to talk to somebody rather than attack them.
   */
  private handleHover(screenX: number, screenY: number): void {
    // Off the world entirely: the pointer is over a panel, or has left the
    // window. Nothing out there is under it, so nothing out there is lit.
    if (screenX < 0 || screenY < 0) {
      this.scene.highlight(null);
      this.scene.showHover(null, false);
      this.callbacks.onSelect(null);
      return;
    }

    const ground = this.scene.pickGround(screenX, screenY);
    this.lastHoveredTile = ground ? { tx: ground.tx, tz: ground.tz } : null;

    // An open hand while something could be picked up, a closed one while it is
    // being carried, which is set where the drag starts.
    const carries = (this.editor?.dragsHeld().length ?? 0) > 0;
    this.canvas.classList.toggle('grabbable', carries && this.carrying.length === 0);

    const target = this.targetsAt(screenX, screenY)[0];

    // While the editor holds the world, the banner says what the pointer would
    // do rather than what a player could do here. They are rarely the same, and
    // the wrong one is worse than none.
    if (this.editor) {
      const object = this.scene.pickObject(screenX, screenY);
      const onTile = object && ground && object.tx === ground.tx && object.tz === ground.tz;
      const named = onTile ? (this.definitions.object(object.id)?.name ?? 'Scenery') : null;

      this.scene.showHover(
        this.lastHoveredTile,
        named !== null,
        this.editor.brushWidth(),
        this.editor.erasing(),
      );

      // A tool that works on things draws a line round the thing under the
      // pointer, so a wall is picked by pointing at the wall. Red for the
      // eraser, since the line is the last thing seen before it goes.
      if (object && this.editor.outlines()) {
        this.scene.highlight(
          { objectId: object.id, index: object.index },
          this.editor.erasing() ? 0xe08a7a : 0xa8f0f7,
        );
      } else {
        this.scene.highlight(null);
      }

      // And what is held is drawn where it would land, rather than after it
      // has landed somewhere nobody meant.
      if (this.areaFrom === null && ground) {
        this.scene.showGhost(this.editor.ghostAt(ground.tx, ground.tz));
      }

      this.callbacks.onSelect(this.editor.describe(named, ground?.tx ?? 0, ground?.tz ?? 0));
      return;
    }

    // Whatever the pointer is over glows in the colour of what a click would do,
    // and the tile marker is left for the one case where a click does nothing
    // but walk: marking the ground under a goblin says the ground is the point.
    const action = target ? this.defaultAction(target) : null;
    const gathered =
      target?.kind === 'object' && this.definitions.object(target.defId)?.gathered === true;
    const meaning = target ? meaningOf(target, action, gathered) : 'walk';

    if (target && meaning !== 'walk') {
      this.scene.highlight(
        target.kind === 'object'
          ? { objectId: target.defId, index: target.index }
          : target.kind === 'npc' || target.kind === 'player'
            ? { entityId: target.entityId }
            : null,
        MEANING_COLOURS[meaning],
      );
    } else {
      this.scene.highlight(null);
    }

    // A pile has no shape of its own worth lighting, so the tile it lies on is
    // still what says where it is. Ground nobody can stand on is not marked at
    // all: a highlight is an offer, and there is nothing on offer there.
    const walkable =
      this.lastHoveredTile !== null &&
      this.state.collision?.isBlocked(
        this.lastHoveredTile.tx,
        this.lastHoveredTile.tz,
        this.state.local.pos.plane,
      ) === false;

    const outlined =
      target?.kind === 'pile'
        ? {
            tx: this.groundItems.get(target.pileId)?.tx ?? 0,
            tz: this.groundItems.get(target.pileId)?.tz ?? 0,
          }
        : meaning === 'walk' && walkable
          ? this.lastHoveredTile
          : null;

    this.scene.showHover(outlined, target?.kind === 'pile');

    if (!target) {
      this.callbacks.onSelect(null);
      return;
    }

    // While something is held for use, what a click would ordinarily do is not
    // what a click is going to do: it is going to use the thing on this. So the
    // name goes up on its own and the panel finishes the sentence with it.
    const name = this.nameOf(target);
    if (this.using || this.casting) {
      this.callbacks.onSelect(name);
      return;
    }

    this.callbacks.onSelect(action ? `${name} \u2013 ${action}` : name);
  }

  /** What a left click on this target does, in the words the menu uses. */
  private defaultAction(target: MenuTarget): string | null {
    switch (target.kind) {
      case 'ground':
        return 'Walk here';
      case 'pile':
        return 'Take';
      case 'player':
        return 'Follow';
      case 'npc':
        return this.definitions.npc(target.defId)?.actions[0] ?? 'Examine';
      case 'object': {
        // What a left click on this one does. A station told to make something
        // does that, so the banner has to say so or it describes a click that
        // is not the click somebody is about to make.
        const told = this.stationDefault?.(target.defId);
        if (told) return told.label;

        return this.definitions.object(target.defId)?.actions[0] ?? 'Examine';
      }
    }
  }

  /** The target's own name, without the level or count the menu title carries. */
  private nameOf(target: MenuTarget): string {
    return target.kind === 'ground' ? 'Ground' : target.name;
  }

  private select(target: MenuTarget): void {
    // The same rule as the hover: with something held for use, the panel is
    // finishing a sentence about it rather than describing a click of its own.
    const name = this.nameOf(target);
    if (this.using) {
      this.callbacks.onSelect(name);
      return;
    }

    const action = this.defaultAction(target);
    this.callbacks.onSelect(action ? `${name} \u2013 ${action}` : name);
  }

  private titleOf(target: MenuTarget): string {
    switch (target.kind) {
      case 'ground':
        return 'Ground';
      case 'object':
        return target.name;
      case 'pile':
        return target.quantity > 1 ? `${target.name} x ${target.quantity}` : target.name;
      case 'npc':
      case 'player':
        return target.combatLevel > 0
          ? `${target.name} (level ${target.combatLevel})`
          : target.name;
    }
  }

  /**
   * What the menu offers about being friends with somebody.
   *
   * Nothing at all where they already are one: an option that does nothing is
   * worse than no option, and this is the one place somebody is looking at a
   * person rather than at a list, so the list is not where they should have to
   * go to find out.
   */
  private friendEntry(name: string): MenuEntry[] {
    switch (this.callbacks.knownAs(name)) {
      case 'friend':
        return [];

      case 'asked':
        return [
          {
            label: `Waiting on ${name}`,
            pending: true,
            run: () => this.callbacks.onSystemMessage(`You have already asked ${name}.`),
          },
        ];

      case 'asking':
        return [
          {
            label: `Accept ${name} as a friend`,
            run: () => this.callbacks.onAskFriend(name),
          },
        ];

      default:
        return [
          { label: `Ask ${name} to be a friend`, run: () => this.callbacks.onAskFriend(name) },
        ];
    }
  }

  /** The menu for a target. The first entry is what a left click does. */
  private entriesFor(target: MenuTarget): MenuEntry[] {
    switch (target.kind) {
      case 'ground':
        return [{ label: 'Walk here', run: () => this.walkTo(target.tx, target.tz, target.plane) }];

      case 'object': {
        const definition = this.definitions.object(target.defId);
        const entries: MenuEntry[] = [];

        // What this kind of station has been told to make, said in full: the
        // count is what the bag can manage right now.
        const told = this.stationDefault?.(target.defId);
        if (told) {
          entries.push({
            label: told.label,
            run: () => this.craftAt(target, told.action, told.count),
          });
        }

        // What this particular one offers, when it has been told. A stretch of
        // water netted and the next one along fished with a rod are the same
        // scenery, so the menu asks the thing standing there rather than its
        // kind. Anything that is not a way of working it is always shown.
        const standing = this.state.region?.objects.find(
          (object) =>
            object.tx === target.tx - (this.state.region?.originX ?? 0) &&
            object.tz === target.tz - (this.state.region?.originZ ?? 0) &&
            (object.plane ?? 0) === target.plane &&
            object.id === target.defId,
        );

        const offered = standing?.ways?.map((way) => way.action.toLowerCase());
        const worked = new Set((definition?.gatherActions ?? []).map((one) => one.toLowerCase()));

        entries.push(
          ...(definition?.actions ?? [])
            .map((action, index) => ({ action, index }))
            .filter(
              ({ action }) =>
                offered === undefined ||
                !worked.has(action.toLowerCase()) ||
                offered.includes(action.toLowerCase()),
            )
            .map(({ action, index }) => ({
              label: `${action} ${target.name}`,
              run: () =>
                this.interact(
                  {
                    kind: TargetKind.Object,
                    id: target.defId,
                    tx: target.tx,
                    tz: target.tz,
                    plane: target.plane,
                    action: index,
                  },
                  { tx: target.tx, tz: target.tz, plane: target.plane },
                ),
            })),
        );

        // No Walk here: the ground under the cursor is a target in its own right and
        // contributes that entry once, at the bottom where it belongs.
        entries.push(this.examineEntry(definition?.examine ?? target.name));
        return entries;
      }

      case 'pile':
        return [
          {
            label: `Take ${target.name}`,
            detail: target.quantity > 1 ? String(target.quantity) : undefined,
            run: () =>
              this.interact(
                {
                  kind: TargetKind.GroundItem,
                  id: target.pileId,
                  tx: 0,
                  tz: 0,
                  plane: this.state.local.pos.plane,
                  action: 0,
                },
                this.pileTile(target.pileId),
              ),
          },
          {
            label: `Examine ${target.name}`,
            run: () =>
              this.callbacks.onSystemMessage(
                this.definitions.item(target.itemId)?.examine ?? target.name,
              ),
          },
        ];

      case 'npc': {
        const definition = this.definitions.npc(target.defId);
        const entries: MenuEntry[] = (definition?.actions ?? []).map((action, index) => ({
          label: `${action} ${target.name}`,
          detail: target.combatLevel > 0 ? `level ${target.combatLevel}` : undefined,
          run: () =>
            this.interact(
              {
                kind: TargetKind.Entity,
                id: target.entityId,
                tx: 0,
                tz: 0,
                plane: this.state.local.pos.plane,
                action: index,
              },
              this.entityTile(target.entityId),
            ),
        }));

        // No Follow here. Trailing a wandering rat around the swamp is not something
        // anyone wants, and an option nobody wants on every entry in the menu costs
        // more than it gives. Following is for other players.
        entries.push(this.examineEntry(definition?.examine ?? target.name));
        return entries;
      }

      case 'player': {
        // Attack is deliberately absent. The starter region is not a player killing
        // zone, and an option that is only ever refused is worse than no option.
        return [
          { label: `Follow ${target.name}`, run: () => this.follow(target.entityId) },
          {
            label: `Message ${target.name}`,
            run: () => this.callbacks.onWhisperTo(target.name),
          },
          { label: `Trade with ${target.name}`, run: () => this.callbacks.onAskTrade(target.name) },
          {
            label: `Ask ${target.name} into your party`,
            run: () => this.callbacks.onAskParty(target.name),
          },
          ...this.friendEntry(target.name),
          {
            label: `Examine ${target.name}`,
            run: () =>
              this.callbacks.onSystemMessage(
                this.examinePlayer(target.entityId, target.name, target.combatLevel),
              ),
          },
          // Last, and quiet about it: it is there when somebody needs it and out
          // of the way of the entries people press every day.
          { label: `Report ${target.name}`, run: () => this.callbacks.onReport(target.name) },
        ];
      }
    }
  }

  // -------------------------------------------------------------------------
  // Items
  // -------------------------------------------------------------------------

  /** The client's copy of a container, created empty the first time it is asked for. */
  container(id: ContainerIdValue): (ItemStack | null)[] {
    let slots = this.containers.get(id);
    if (!slots) {
      const size = containerSize(id);
      slots = new Array<ItemStack | null>(size).fill(null);
      this.containers.set(id, slots);
    }
    return slots;
  }

  /**
   * Dragging an item onto another slot.
   *
   * Drawn immediately and sent as intent. The server answers with both slots
   * whether it agreed or not, so a move it refuses snaps back on its own.
   */
  moveItem(container: ContainerIdValue, from: number, to: number): void {
    const slots = this.container(container);
    if (from === to || from < 0 || to < 0 || from >= slots.length || to >= slots.length) return;

    const carried = slots[from] ?? null;
    slots[from] = slots[to] ?? null;
    slots[to] = carried;

    this.callbacks.onContainer(container, slots);
    this.connection.sendMoveItem(container, from, to);
  }

  /**
   * Left clicking an item runs its first menu entry, matching how a left click on
   * anything else in the world runs the first entry of that thing's menu.
   */
  useItem(container: ContainerIdValue, slot: number): void {
    const stack = this.container(container)[slot] ?? null;
    if (!stack) return;

    if (container === ContainerId.Bank) {
      this.connection.sendBankMove(false, slot, 1);
      return;
    }

    // Clicking something worn takes it off, which is its only entry.
    if (container === ContainerId.Equipment) {
      this.connection.sendUnequip(slot);
      return;
    }

    // With the bank open, a click puts it away rather than using it.
    if (this.bankOpen && container === ContainerId.Inventory) {
      this.connection.sendBankMove(true, slot, 0);
      return;
    }

    const definition = this.definitions.item(stack.id);
    if (!definition || definition.actions.length === 0) return;

    this.connection.sendItemAction(container, slot, 0);
  }

  /**
   * The tile under the pointer, for the development tools.
   *
   * The tools work on where the person is looking rather than on typed numbers,
   * which is the whole difference between editing a map and editing a file.
   */
  hoveredTile(): { tx: number; tz: number } | null {
    return this.lastHoveredTile;
  }

  /** The menu for a held item. */
  openItemMenu(screenX: number, screenY: number, container: ContainerIdValue, slot: number): void {
    const stack = this.container(container)[slot] ?? null;
    if (!stack) {
      this.callbacks.onContextMenu(null);
      return;
    }

    const definition = this.definitions.item(stack.id);
    const name = definition?.name ?? `Item ${stack.id}`;

    const entries: MenuEntry[] = [];

    /*
     * Where another one comes from and what it is good for.
     *
     * The one question a line of examine text never answers, and the same
     * entry wherever an item is held: in the bag, in the bank, on the belt and
     * on the body. Three of those return early, which is why this is made once
     * rather than written out at the bottom.
     */
    const lookUp: MenuEntry = {
      label: `Look up ${name}`,
      run: () => this.callbacks.onLookUp(stack.id),
    };

    if (container === ContainerId.Bank) {
      for (const amount of [1, 5, 10]) {
        entries.push({
          label: `Withdraw ${amount}`,
          run: () => this.connection.sendBankMove(false, slot, amount),
        });
      }
      entries.push({
        label: 'Withdraw all',
        run: () => this.connection.sendBankMove(false, slot, 0),
      });

      // Straight onto the body, swapping out whatever is in that slot already.
      if (definition?.equipSlot !== undefined && definition.equipSlot >= 0) {
        entries.push({
          label: `Wear ${name}`,
          run: () => this.connection.sendBankMove(false, slot, 1, { kit: true }),
        });
      }
      entries.push(lookUp, this.examineEntry(definition?.examine ?? name));
      this.callbacks.onContextMenu({ screenX, screenY, title: name, entries });
      return;
    }

    if (container === ContainerId.Toolbelt) {
      entries.push({
        label: `Take ${name} off the belt`,
        run: () => this.connection.sendToolbelt(false, slot),
      });
      entries.push(lookUp, this.examineEntry(definition?.examine ?? name));
      this.callbacks.onContextMenu({ screenX, screenY, title: name, entries });
      return;
    }

    if (container === ContainerId.Equipment) {
      entries.push({ label: `Remove ${name}`, run: () => this.connection.sendUnequip(slot) });
    } else {
      for (const [index, action] of (definition?.actions ?? []).entries()) {
        entries.push({
          label: `${action} ${name}`,
          run: () => this.connection.sendItemAction(container, slot, index),
        });
      }

      // Everything can be used, whether or not there is anything to use it on.
      // Picking it up here is half of the gesture; the other half is the next
      // click, wherever it lands.
      entries.push({ label: `Use ${name}`, run: () => this.holdForUse(slot, name) });

      if (this.bankOpen) {
        // Several of something that does not stack means several slots, so the
        // count is asked for across the bag rather than out of this one slot.
        const spread = stack.quantity === 1;

        for (const amount of [1, 5, 10]) {
          entries.push({
            label: `Deposit ${amount}`,
            run: () => this.connection.sendBankMove(true, slot, amount, { every: spread }),
          });
        }
        entries.push({
          label: 'Deposit all',
          run: () => this.connection.sendBankMove(true, slot, 0, { every: spread }),
        });
      }

      if (this.shopOpen) {
        for (const amount of [1, 5, 10]) {
          entries.push({
            label: `Sell ${amount}`,
            run: () => this.connection.sendShopTrade(false, slot, amount),
          });
        }
      }

      // Anything that does a job goes on the belt, where it stops being luggage.
      if (toolbeltSlotFor(definition?.tags ?? []) !== null) {
        entries.push({
          label: 'Add to Toolbelt',
          run: () => this.connection.sendToolbelt(true, slot),
        });
      }

      entries.push({
        label: `Drop ${name}`,
        run: () => {
          // Anything worth more than somebody said they would drop without
          // thinking asks once more, in the same menu, where the pointer is.
          const worth = (definition?.value ?? 0) * stack.quantity;
          const over = this.settings.confirmDropsOver;
          if (over > 0 && worth > over) {
            this.callbacks.onContextMenu({
              screenX,
              screenY,
              title: `Drop ${name}? It is worth ${worth.toLocaleString()} coins.`,
              entries: [
                { label: 'Drop it', run: () => this.connection.sendDropItem(container, slot) },
                { label: 'Keep it', run: () => undefined },
              ],
            });
            return;
          }
          this.connection.sendDropItem(container, slot);
        },
      });
    }
    entries.push(lookUp);

    entries.push({
      label: `Examine ${name}`,
      run: () => this.callbacks.onSystemMessage(definition?.examine ?? name),
    });

    this.callbacks.onContextMenu({ screenX, screenY, title: name, entries });
  }

  /** What another player is and what they are wearing, as one line. */
  private examinePlayer(entityId: number, name: string, combatLevel: number): string {
    const entity = this.state.entities.get(entityId);
    const worn: string[] = [];

    for (const itemId of entity?.equipment ?? []) {
      if (itemId === 0) continue;
      worn.push(this.definitions.item(itemId)?.name ?? `Item ${itemId}`);
    }

    const gear = worn.length > 0 ? `Wearing ${worn.join(', ')}.` : 'Wearing nothing worth noting.';
    return `${name} is a level ${combatLevel} adventurer. ${gear}`;
  }

  /** The tile a pile is on, for walking to it. */
  private pileTile(pileId: number): TilePos | null {
    const pile = this.groundItems.get(pileId);
    return pile ? { tx: pile.tx, tz: pile.tz, plane: pile.plane } : null;
  }

  /** The tile an entity is standing on this moment. */
  private entityTile(entityId: number): TilePos | null {
    const entity = this.state.entities.get(entityId);
    return entity ? tileOf({ x: entity.x, z: entity.z, plane: entity.plane }) : null;
  }

  private examineEntry(text: string): MenuEntry {
    return { label: 'Examine', run: () => this.callbacks.onSystemMessage(text) };
  }

  /**
   * Making something at a station, and walking there while the word travels.
   *
   * The same journey an ordinary interaction makes: without the walk being
   * predicted here, the client stands still while the server walks, and the
   * character is dragged across the ground by the corrections that follow.
   */
  private craftAt(
    target: { defId: number; tx: number; tz: number; plane: number },
    action: string,
    count: number,
  ): void {
    const goal = { tx: target.tx, tz: target.tz, plane: target.plane };

    this.walkBeside(goal);
    this.facing = { tx: goal.tx, tz: goal.tz };

    this.connection.sendCraft(target.defId, goal, action, count);
  }

  /**
   * Sends an interaction, and walks towards the target while it travels.
   *
   * The server walks the player to the same tile with the same search, so the walk
   * is predicted like any other movement. Leaving it to the server alone means the
   * client predicts standing still and is dragged along by position corrections,
   * which is what made interacting with something across the clearing lurch.
   */
  private interact(packet: InteractPacket, goal: TilePos | null): void {
    // Something on the ground is walked onto; everything else is walked up to.
    // The server judges arrival the same way, so the two agree about when the
    // hand actually reaches it.
    if (goal) {
      // Anything on the water is worked at from a side rather than a corner,
      // and the server walks to the same tile for the same reason.
      const square =
        packet.kind === TargetKind.Object && this.definitions.object(packet.id)?.afloat === true;

      if (packet.kind === TargetKind.GroundItem) this.walkOnto(goal);
      else if (this.reach > 1 && this.isAttack(packet)) {
        this.walkToShoot(goal);

        // Kept up with from the start rather than from the first shot, because
        // the server re-aims the approach every tick while a target wanders and
        // a prediction aimed only once would part company with it.
        this.fighting = packet.id;
        this.fightTile = null;
        this.lastBlow = performance.now();
      } else this.walkBeside(goal, square);
    }

    // What is being dealt with, so the character turns to face it the moment they
    // arrive rather than a round trip later. Something alive is remembered by id,
    // because it can move while being talked to or fought.
    this.facing =
      packet.kind === TargetKind.Entity
        ? { entityId: packet.id }
        : goal
          ? { tx: goal.tx, tz: goal.tz }
          : null;

    this.connection.sendInteract(packet);
  }

  /**
   * What the character is dealing with, and so what they should be looking at.
   *
   * The server turns everybody authoritatively, but a client draws its own
   * character from its own prediction, and waiting for a packet to turn on the
   * spot reads as a delay. Cleared by walking somewhere, because going somewhere
   * else is the end of dealing with a thing.
   */
  private facing: { entityId: number } | { tx: number; tz: number } | null = null;

  /**
   * Turns the character towards whatever they are dealing with.
   *
   * Only while they are standing still: the direction of a walk is the direction
   * they walk in, and a character that swivels mid stride reads as broken.
   */
  private faceTarget(): void {
    if (!this.facing || this.state.local.path.length > 0) return;

    const at =
      'entityId' in this.facing
        ? this.state.entities.get(this.facing.entityId)
        : { x: this.facing.tx + 0.5, z: this.facing.tz + 0.5 };

    if (!at) {
      this.facing = null;
      return;
    }

    const here = this.state.local.pos;
    const dx = at.x - here.x;
    const dz = at.z - here.z;
    if (Math.hypot(dx, dz) < 0.15) return;

    this.state.local.heading = headingOf(dx, dz);
  }

  /** Walks onto a tile, for something lying on it. */
  private walkOnto(goal: TilePos): void {
    const from = tileOf(this.state.local.pos);
    if (sameTile(from, goal)) return;

    this.stopFollowing();
    if (this.state.pathTo(goal.tx, goal.tz, goal.plane)) {
      this.scene.showClickMarker(goal.tx + 0.5, goal.tz + 0.5);
    }
  }

  /** Walks to the free tile next to a target, matching what the server will do. */
  private walkBeside(goal: TilePos, square = false): void {
    const collision = this.state.collision;
    if (!collision) return;

    const from = tileOf(this.state.local.pos);
    if (withinReach(from, goal)) return;

    const beside = tileBeside(collision, goal, from, square);
    if (!beside) return;

    this.stopFollowing();
    if (this.state.pathTo(beside.tx, beside.tz, beside.plane)) {
      this.scene.showClickMarker(beside.tx + 0.5, beside.tz + 0.5);
    }
  }

  /** Whether an interaction is an attack, which is walked to by reach rather than to arm's length. */
  private isAttack(packet: InteractPacket): boolean {
    if (packet.kind !== TargetKind.Entity) return false;

    const entity = this.state.entities.get(packet.id);
    if (!entity) return false;

    // Creatures only: other players are never interacted with this way, and a
    // creature's menu says which of its entries is the fight.
    if (entity.kind !== 1) return false;
    const action = this.definitions.npc(entity.defId)?.actions[packet.action] ?? '';
    return /attack/i.test(action);
  }

  /**
   * Walks to somewhere a target can be shot from, matching what the server will do.
   *
   * The shared `routeToShoot`, run against this client's copy of the same maps,
   * so the walk ends on the tile the server's does. Predicting a walk up beside
   * the target instead is what had archers striding in to melee range and being
   * dragged back out to seven tiles by the corrections.
   */
  private walkToShoot(goal: TilePos, reach = this.reach): void {
    const collision = this.state.collision;
    const sight = this.state.sight;
    if (!collision || !sight) return;

    const route = routeToShoot(collision, sight, tileOf(this.state.local.pos), goal, reach);
    if (!route || route.length === 0) return;

    this.stopFollowing();
    const last = route[route.length - 1];
    if (this.state.walkRoute(route) && last) this.scene.showClickMarker(last.x, last.z);
  }

  /**
   * One step of keeping station on something being shot at.
   *
   * Stands still while the shot is there, keeps walking while the walk still
   * ends somewhere with a shot, and only then looks for somewhere new. The server
   * asks the same three questions in the same order.
   */
  private keepShooting(goal: TilePos): void {
    const collision = this.state.collision;
    const sight = this.state.sight;
    if (!collision || !sight) return;

    // Somewhere with a shot: stop on this tile, as the server does the moment
    // one comes on, rather than walking on to wherever the route was going.
    const from = tileOf(this.state.local.pos);
    if (canShootFrom(collision, sight, from, goal, this.reach)) {
      if (this.state.local.path.length > 0) haltAtTile(this.state.local);
      return;
    }

    const path = this.state.local.path;
    const end = path.length > 0 ? path[path.length - 1] : null;
    if (end) {
      const endTile = { tx: Math.floor(end.x), tz: Math.floor(end.z), plane: from.plane };
      if (canShootFrom(collision, sight, endTile, goal, this.reach)) return;
    }

    const route = routeToShoot(collision, sight, from, goal, this.reach);
    if (route && route.length > 0) this.state.walkRoute(route);
  }

  private walkTo(tx: number, tz: number, plane: number): void {
    // Walking somewhere is the end of dealing with whatever was being dealt
    // with, a fight included: the server breaks it off when a route arrives, and
    // a client that kept chasing would spend the walk arguing with it. It is
    // also the end of any shot the world was holding: somebody who has decided
    // to go somewhere has decided the scene is over.
    this.facing = null;
    this.fighting = null;
    if (this.heldShot) this.followCharacter();

    // Predict the path locally with the same search the server runs, so walking
    // starts on this frame rather than a round trip later.
    // Where the walk really ends, which is not always where the click landed:
    // clicking the far bank of a river walks to the near one, and clicking the
    // water while standing beside it walks nowhere at all. Both are worth saying
    // out loud rather than leaving somebody to watch and work out.
    const walking = this.state.pathTo(tx, tz, plane);
    const last = walking
      ? this.state.route[this.state.route.length - 1]
      : tileOf(this.state.local.pos);
    const stop = last ? { x: last.tx + 0.5, z: last.tz + 0.5 } : undefined;

    this.scene.showClickMarker(tx + 0.5, tz + 0.5, stop);

    this.following = null;
    this.connection.sendMoveTo(tx, tz, plane, this.state.route);
  }

  /**
   * Holding an item, waiting for whatever it is going to be used on.
   *
   * The next click decides: another slot, a piece of scenery, somebody standing
   * there. Escape or a second click on the same slot puts it down again.
   */
  holdForUse(slot: number, name: string): void {
    this.using = { slot, name };
    this.callbacks.onUsing({ slot, name });

    // And the panel starts the sentence again. Whatever it was saying was about
    // a click that is no longer the click on offer: "Use a hatchet on Ground,
    // walk here" is two answers to two different questions.
    this.callbacks.onSelect(null);
  }

  /**
   * A spell pressed in the book, waiting for whatever it is to be cast at.
   *
   * The next click decides, the way it does for an item held for use: somebody
   * for a spell cast at somebody, a place for one cast at the ground, a slot in
   * the bag for one cast on an item. Escape puts it down.
   */
  beginCasting(spell: SpellWire): void {
    this.stopUsing();
    this.casting = spell;
    this.callbacks.onCasting(spell);
    this.callbacks.onSelect(null);
  }

  /** Puts down the spell waiting for a target, without casting it. */
  stopCasting(): void {
    if (!this.casting) return;
    this.casting = null;
    this.callbacks.onCasting(null);
  }

  /** A spell with nothing to choose: cast on the caster at once. */
  castOnSelf(spell: SpellWire): void {
    this.connection.sendCastSpell({
      spell: spell.id,
      target: CastTarget.Self,
      id: 0,
      tx: 0,
      tz: 0,
      plane: 0,
    });
  }

  /** The spell waiting for a target, cast at whatever was clicked. */
  private castOn(target: MenuTarget): boolean {
    const spell = this.casting;
    if (!spell) return false;
    this.stopCasting();

    const plane = this.state.local.pos.plane;

    if (spell.target === 'tile') {
      const at =
        target.kind === 'ground' || target.kind === 'object'
          ? { tx: target.tx, tz: target.tz, plane }
          : target.kind === 'pile'
            ? this.pileTile(target.pileId)
            : this.entityTile(target.entityId);
      if (!at) return true;

      this.connection.sendCastSpell({
        spell: spell.id,
        target: CastTarget.Tile,
        id: 0,
        tx: at.tx,
        tz: at.tz,
        plane,
      });
      this.walkToShoot(at, spell.range);
      this.facing = { tx: at.tx, tz: at.tz };
      return true;
    }

    if (spell.target === 'enemy' || spell.target === 'friend') {
      if (target.kind !== 'npc' && target.kind !== 'player') {
        this.callbacks.onSystemMessage(`${spell.name} has to be cast at somebody.`);
        return true;
      }

      this.connection.sendCastSpell({
        spell: spell.id,
        target: CastTarget.Entity,
        id: target.entityId,
        tx: 0,
        tz: 0,
        plane,
      });
      const goal = this.entityTile(target.entityId);
      if (goal) this.walkToShoot(goal, spell.range);
      this.facing = { entityId: target.entityId };
      return true;
    }

    if (spell.target === 'item') {
      this.callbacks.onSystemMessage(`${spell.name} is cast on something in your bag.`);
    }
    return true;
  }

  /** The spell waiting for a target, cast on a slot in the bag, which the interface asks for directly. */
  castOnSlot(slot: number): boolean {
    const spell = this.casting;
    if (!spell) return false;
    this.stopCasting();

    if (spell.target === 'item') {
      this.connection.sendCastSpell({
        spell: spell.id,
        target: CastTarget.Item,
        id: slot,
        tx: 0,
        tz: 0,
        plane: 0,
      });
    } else {
      this.callbacks.onSystemMessage(`${spell.name} is not cast on things in your bag.`);
    }
    return true;
  }

  // -------------------------------------------------------------------------
  // Sounds
  // -------------------------------------------------------------------------

  /** The one sound engine, reachable from wherever the game is. */
  readonly sounds = sounds;

  /** Where the last footstep fell, and how far the feet have gone since. */
  private stepFrom: { x: number; z: number } | null = null;
  private walkedSinceStep = 0;
  /** When the background was last weighed up. */
  private ambientAt = 0;
  /** The prayers held up last time, so taking one up or letting one go can be heard. */
  private heldPrayers = new Set<number>();

  /**
   * A blow heard: the swing if it was close enough to be one, then whether it
   * landed, and a character's own sounds where it has them.
   */
  private hitSounds(entityId: number, fromId: number, damage: number): void {
    const target = this.placeOf(entityId);
    const striker = fromId > 0 ? this.placeOf(fromId) : null;
    const npcOf = (id: number): number | null => {
      const entity = this.state.entities.get(id);
      return entity && entity.kind === WireEntityKind.Npc ? entity.defId : null;
    };

    if (
      striker &&
      target &&
      Math.max(Math.abs(striker.x - target.x), Math.abs(striker.z - target.z)) < 1.8
    ) {
      const npc = npcOf(fromId);
      if (npc !== null) sounds.npcSound(npc, 'attack', 'swing', striker);
      else sounds.cue('swing', striker);
    }

    if (damage > 0) {
      const npc = npcOf(entityId);
      if (npc !== null) sounds.npcSound(npc, 'hurt', 'hit', target);
      else sounds.cue('hit', target);
      if (entityId === this.state.localId) sounds.cue('hurt');
    } else {
      sounds.cue('block', target);
    }
  }

  /** Where something is for the sake of hearing it, if it is anywhere at all. */
  private placeOf(entityId: number): SoundPlace | null {
    if (entityId === this.state.localId) return this.state.local.pos;
    const entity = this.state.entities.get(entityId);
    return entity ? { x: entity.x, z: entity.z } : null;
  }

  /**
   * Once a frame: the ears go where the camera looks, feet make a sound every
   * stride, and every second the background is weighed up again.
   */
  private listen(focus: WorldPos, time: number): void {
    sounds.setListener(focus.x, focus.z, this.camera.yaw);

    const here = this.state.local.pos;
    if (this.stepFrom) {
      const moved = Math.hypot(here.x - this.stepFrom.x, here.z - this.stepFrom.z);
      // A jump of more than a few tiles is being put somewhere, not walking there.
      if (moved > 3) this.walkedSinceStep = 0;
      else this.walkedSinceStep += moved;
    }
    this.stepFrom = { x: here.x, z: here.z };

    const stride = this.state.local.running ? 1.15 : 0.85;
    if (this.walkedSinceStep >= stride) {
      this.walkedSinceStep = 0;
      sounds.cue(this.footstepCue(), null, 0.8);
    }

    if (time - this.ambientAt > 1000) {
      this.ambientAt = time;
      const day = this.lightNow();
      const sea = this.seaNearby(focus);
      sounds.setAmbient({
        'ambient-day': Math.max(0, day - 0.2) * (1 - sea * 0.6),
        'ambient-night': Math.max(0, 0.8 - day) * (1 - sea * 0.6),
        'ambient-sea': sea,
      });
    }
  }

  /** The ground under the character, heard as a footstep. */
  private footstepCue(): string {
    const region = this.state.region;
    if (!region) return 'step-grass';
    const pos = this.state.local.pos;
    const lx = Math.floor(pos.x) - region.originX;
    const lz = Math.floor(pos.z) - region.originZ;
    if (lx < 0 || lz < 0 || lx >= region.width || lz >= region.height) return 'step-grass';

    const at = lz * region.width + lx;
    const flags = region.flags[at] ?? 0;
    const onBoards =
      (flags & CollisionFlag.Decked) !== 0 || pos.plane > 0 || this.scene.hasFloorAt(pos.x, pos.z);

    // Wading: the ground under the feet is below the water beside it.
    const stride = region.width + 1;
    const corners = [
      region.heights[lz * stride + lx] ?? 0,
      region.heights[lz * stride + lx + 1] ?? 0,
      region.heights[(lz + 1) * stride + lx] ?? 0,
      region.heights[(lz + 1) * stride + lx + 1] ?? 0,
    ];
    const ground = corners.reduce((sum, one) => sum + one, 0) / 4;
    let surface = -Infinity;
    for (const [dx, dz] of [
      [0, 0],
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const x = lx + dx;
      const z = lz + dz;
      if (x < 0 || z < 0 || x >= region.width || z >= region.height) continue;
      const index = z * region.width + x;
      if (((region.flags[index] ?? 0) & CollisionFlag.Water) === 0) continue;
      surface = Math.max(surface, region.water?.[index] ?? -Infinity);
    }
    const wading = !onBoards && ground < surface - 0.05;

    const kind = region.ground?.[at] ?? 0;
    const name =
      kind === GroundKind.Auto
        ? ground < 1.2
          ? 'sand'
          : 'grass'
        : (
            Object.entries(GroundKind).find(([, value]) => value === kind)?.[0] ?? 'Grass'
          ).toLowerCase();
    return stepCue(name, wading, onBoards);
  }

  /** How much of what is round the listener is sea, nought to one. */
  private seaNearby(focus: WorldPos): number {
    const region = this.state.region;
    if (!region) return 0;
    const cx = Math.floor(focus.x) - region.originX;
    const cz = Math.floor(focus.z) - region.originZ;
    let wet = 0;
    let looked = 0;
    for (let dz = -10; dz <= 10; dz += 2) {
      for (let dx = -10; dx <= 10; dx += 2) {
        const x = cx + dx;
        const z = cz + dz;
        if (x < 0 || z < 0 || x >= region.width || z >= region.height) continue;
        looked++;
        const index = z * region.width + x;
        // The sea is the water at the level of the sea; a lake up a hill is not it.
        if (
          ((region.flags[index] ?? 0) & CollisionFlag.Water) !== 0 &&
          (region.water?.[index] ?? 1) <= 0.05
        ) {
          wet++;
        }
      }
    }
    return looked === 0 ? 0 : Math.min(1, (wet / looked) * 2.5);
  }

  /** Every spell, for drawing what flies when one is cast. */
  private spellbook = new Map<number, SpellWire>();

  /** The model library, for a spell whose projectile is one of them. */
  private modelLibrary: readonly ModelWire[] = [];

  /** A spell pressed and not yet cast, if there is one. */
  private casting: SpellWire | null = null;

  /**
   * Whether something lasting holds the character where they stand. Told by the
   * server, so the walk is not predicted and then pulled back.
   */
  private bound = false;

  /** Puts down whatever was being used, without using it. */
  stopUsing(): void {
    if (!this.using) return;
    this.using = null;
    this.callbacks.onUsing(null);
  }

  /** Every recipe there is, kept from the definitions. */
  private recipes: readonly RecipeWire[] = [];

  /** What is held for use, if anything. */
  private using: { slot: number; name: string } | null = null;

  /**
   * What a station has been told to make, asked of the interface.
   *
   * The preference belongs to the person playing and is kept where their other
   * preferences are, so the game asks for it rather than holding it.
   */
  stationDefault:
    ((objectId: number) => { label: string; action: string; count: number } | null) | null = null;

  /**
   * Using what is held on whatever was clicked.
   *
   * Everything goes to the server, including the pairs that come to nothing: it
   * is the one that knows what a fire does with a fish, and the reply for the
   * rest of them is a line saying nothing happened.
   */
  private useOn(target: MenuTarget): boolean {
    const held = this.using;
    if (!held) return false;

    this.stopUsing();

    switch (target.kind) {
      case 'object':
        // Shift held means keep at it: feeding a fire one log at a time is how
        // somebody trains, and one click per log is how they stop.
        this.connection.sendUseOn(
          held.slot,
          {
            kind: TargetKind.Object,
            id: target.defId,
            tx: target.tx,
            tz: target.tz,
            plane: target.plane,
          },
          this.input.removingFromSelection,
        );
        return true;

      case 'npc':
      case 'player':
        this.connection.sendUseOn(held.slot, {
          kind: TargetKind.Entity,
          id: target.entityId,
          tx: 0,
          tz: 0,
          plane: 0,
        });
        return true;

      default:
        this.callbacks.onSystemMessage('Nothing happened.');
        return true;
    }
  }

  /** Using what is held on another slot, which the interface asks for directly. */
  useOnSlot(slot: number): boolean {
    const held = this.using;
    if (!held) return false;

    this.stopUsing();
    if (held.slot === slot) return true;

    this.connection.sendUseOn(held.slot, {
      kind: TargetKind.Item,
      id: slot,
      tx: 0,
      tz: 0,
      plane: 0,
    });
    return true;
  }

  /** Starts following another player. Only players can be followed. */
  private follow(entityId: number): void {
    const target = this.state.entities.get(entityId);
    if (!target || target.kind !== WireEntityKind.Player) return;
    this.following = entityId;
    this.followRepathAt = 0;
    this.followingTile = null;
    this.followTrail = [];
  }

  /**
   * One step of keeping up with something being fought.
   *
   * The server does exactly this, and the point of doing it here as well is that
   * the two then walk the same line at the same time. Without it a fight that
   * moves is walked entirely by the server, and this client spends it being
   * dragged along a tile and a half behind, which is what the drift at the end
   * of a chase was.
   *
   * Nothing is sent. Asking to walk somewhere is how a fight is broken off, so a
   * chase that announced itself would end the fight it is part of.
   */
  private keepUpWithFight(): void {
    const target = this.fighting === null ? undefined : this.state.entities.get(this.fighting);

    // A walk into bow range can take longer than a fight takes to go quiet, so
    // still being on the way counts as the fight carrying on.
    if (this.reach > 1 && this.state.local.path.length > 0) this.lastBlow = performance.now();

    // Gone, or quiet for long enough that whatever it was is over.
    if (!target || performance.now() - this.lastBlow > FIGHT_FORGOTTEN_MS) {
      this.fighting = null;
      return;
    }

    const collision = this.state.collision;
    if (!collision) return;

    // Between steps only, for the same reason a follower waits: a route redone
    // mid stride gives back the ground already covered.
    if (this.state.local.path.length > 0 && !isCentred(this.state.local.pos)) return;

    const from = tileOf(this.state.local.pos);
    const goal = tileOf({ x: target.x, z: target.z, plane: from.plane });

    // From a distance, the same rule the server keeps station by, so the two
    // stand in the same place rather than this client walking up to swing.
    if (this.reach > 1) {
      this.fightTile = goal;
      this.keepShooting(goal);
      return;
    }

    // In reach is where a fight happens: stand and swing.
    if (tileDistance(from, goal) <= 1) {
      this.fightTile = goal;
      return;
    }

    if (this.fightTile && sameTile(this.fightTile, goal)) return;
    this.fightTile = goal;

    const beside = tileBeside(collision, goal, from);
    if (beside) this.state.pathTo(beside.tx, beside.tz, from.plane);
  }

  /** Stops following, which any deliberate movement does. */
  stopFollowing(): void {
    this.following = null;
  }

  /**
   * One step of following somebody.
   *
   * The destination is the free tile beside them nearest to here, so a follower
   * ends up touching their target rather than merely near them. Corners count as
   * touching, the way they do everywhere else in the game, which is why a
   * follower cuts a corner instead of walking around it.
   */
  private keepUpWithTarget(): void {
    const target = this.following === null ? undefined : this.state.entities.get(this.following);
    if (!target) {
      if (this.following !== null) {
        this.stopFollowing();
        this.callbacks.onSystemMessage('You have lost them.');
      }
      return;
    }

    const collision = this.state.collision;
    if (!collision) return;

    // Only ever between steps. A route worked out mid stride starts from the
    // tile being walked into, which throws away the part of the step already
    // walked: doing that every time the leader moves halves the follower's pace.
    if (this.state.local.path.length > 0 && !isCentred(this.state.local.pos)) return;

    const from = tileOf(this.state.local.pos);
    const goal = tileOf({ x: target.x, z: target.z, plane: from.plane });

    // Where they were last seen, kept as they go, so a follower walks the ground
    // the leader walked rather than cutting whatever corner the search prefers.
    // Doubling back is the one case where their trail is the wrong answer, so
    // anything already passed is dropped rather than walked twice.
    const trail = this.followTrail;
    const last = trail[trail.length - 1];
    if (!last || !sameTile(last, goal)) {
      const already = trail.findIndex((step) => sameTile(step, goal));
      if (already >= 0) trail.length = already;

      trail.push(goal);
      if (trail.length > FOLLOW_TRAIL) trail.shift();
    }

    if (this.followingTile && sameTile(this.followingTile, goal)) return;

    const now = performance.now();
    if (now - this.followRepathAt < FOLLOW_REPATH_MS) return;
    this.followRepathAt = now;
    this.followingTile = goal;

    // Right behind them: the tile they were on before this one, which is the
    // ground they have just left and the place somebody following actually
    // walks. If that is not to be had, the nearest tile touching them will do.
    const behind = trail.length >= 2 ? trail[trail.length - 2] : undefined;
    const wanted =
      behind && !collision.isBlocked(behind.tx, behind.tz, from.plane)
        ? behind
        : (tileBeside(collision, goal, from) ?? goal);

    // Already there: stand still and look at them rather than shuffling.
    if (sameTile(from, wanted)) {
      this.facing = { entityId: target.id };
      return;
    }

    if (tileDistance(from, goal) <= 1 && sameTile(wanted, goal)) return;

    if (this.state.pathTo(wanted.tx, wanted.tz, from.plane)) {
      this.connection.sendMoveTo(wanted.tx, wanted.tz, from.plane, this.state.route);
    }

    // Facing them is what makes a follower read as following rather than as
    // somebody who happens to be walking the same way.
    this.facing = { entityId: target.id };
  }

  /** Where whoever is being followed has been, most recent last. */
  private followTrail: TilePos[] = [];

  whisper(to: string, message: string): void {
    this.connection.sendPrivateMessage(to, message);
  }

  private toggleRun(): void {
    this.setRunning(!this.state.local.running);
  }

  setRunning(wanted: boolean): void {
    const running = runAllowed(this.state.local, wanted);
    if (wanted && !running) {
      this.callbacks.onSystemMessage('You are too tired to run.');
      return;
    }
    if (this.state.local.running === running) return;

    this.state.local.running = running;
    this.toggledRunAt = performance.now();
    this.connection.sendRunning(running);
    this.callbacks.onRunEnergy(this.state.local.runEnergy, running);
  }

  /** When run was last switched by hand, so a packet already on its way does not undo it. */
  private toggledRunAt = 0;

  localTile(): WorldPos {
    return this.state.renderPosition();
  }

  localTileCoords(): { tx: number; tz: number } {
    const tile = tileOf(this.state.local.pos);
    return { tx: tile.tx, tz: tile.tz };
  }

  // -------------------------------------------------------------------------
  // Server events
  // -------------------------------------------------------------------------

  private bindConnection(): void {
    this.connection.on('welcome', (packet) => {
      this.state.localId = packet.entityId;
      // The scene needs it too: a blow landing on the character being played
      // arrives by the same id everybody else's does.
      this.scene.setLocalId(packet.entityId);
    });

    this.connection.on('region', (packet) => {
      // Somewhere else entirely, rather than the same place sent again: everything
      // known about the old one goes, or a dungeon opens full of people standing
      // where they were on the island.
      const elsewhere = this.state.region !== null && this.state.region.id !== packet.id;

      this.state.applyRegion(packet);
      // The region says how large a place it is; the ground itself arrives a
      // square at a time from here on, so what is built now is an empty place
      // of the right size and the squares fill it in.
      if (this.state.region) this.scene.buildRegion(this.state.region);
      this.floatingAt = null;

      if (elsewhere) {
        this.state.despawn([...this.state.entities.keys()]);
        this.scene.syncEntities(this.state);
        this.groundItems.clear();
        this.following = null;
        this.viewFocus = null;
      }

      // Piles sit on the terrain, so anything already known is placed again against
      // the new heights rather than left floating over the old ones.
      this.scene.clearGroundItems();
      for (const pile of this.groundItems.values()) this.scene.addGroundItem(pile);
    });

    // One square of ground. Everything that reads the ground reads the same
    // arrays it always did; this writes into them and says which part changed.
    this.connection.on('ground', (packet) => {
      this.state.applyGround(packet);
      if (this.state.region) this.scene.groundArrived(this.state.region, packet.tx, packet.tz);

      // Piles and anything else standing on ground that has only just arrived
      // were placed against nothing. Put them back against what is there now.
      this.scene.clearGroundItems();
      for (const pile of this.groundItems.values()) this.scene.addGroundItem(pile);
    });

    this.connection.on('groundGone', (packet) => {
      this.state.forgetGround(packet);
      if (this.state.region) {
        for (const square of packet.squares) {
          this.scene.groundGone(this.state.region, square.tx, square.tz);
        }
      }
    });

    this.connection.on('playerState', (packet) => {
      this.state.applyPlayerState(packet);
      this.callbacks.onRunEnergy(packet.runEnergy, packet.running);
      this.callbacks.onSkills(packet.levels, packet.experience);
      this.callbacks.onLook(packet.look);
    });

    this.connection.on('entitySpawn', (packet) => this.state.spawn(packet));
    this.connection.on('entityDespawn', (ids) => this.state.despawn(ids));
    this.connection.on('positions', (updates) => this.state.positions(updates));
    this.connection.on('correction', (packet) => {
      this.state.applyCorrection(packet);
      this.callbacks.onRunEnergy(packet.runEnergy, this.state.local.running);
    });
    this.connection.on('definitions', (packet) => {
      this.definitions.apply(packet);
      this.modelLibrary = packet.models;
      this.recipes = packet.recipes;
      this.state.setBarriers(packet.objects);
      this.scene.setFloors(
        packet.objects.filter((object) => object.floor).map((object) => object.id),
      );
      this.scene.setRoofs(
        packet.objects.filter((object) => object.roof).map((object) => object.id),
      );
      setBuiltShapes(packet.objects.map((object) => [object.id, object.parts] as [number, string]));
      this.scene.setBorrowedShapes(
        packet.objects.map((object) => [object.id, object.looksLike] as [number, number]),
      );
      // Which clip each pose plays from, before anything is drawn with it: a
      // body already on screen is redressed the next time it is loaded.
      usePoses(packet.poses);

      // Everything standing in the world is drawn again from the shapes that
      // just arrived. Without this a tree reshaped in the tools stays the old
      // tree for everybody already looking at it, and the only way to see the
      // change is to reload, which is the thing this is here to stop.
      this.scene.redrawScenery();

      this.callbacks.onDefinitions(packet);
    });
    this.connection.on('latency', (ms) => {
      this.state.latency = ms;
    });

    this.connection.on('admin', (packet) => this.callbacks.onAdmin(packet));
    this.connection.on('adminFacts', (packet) => this.callbacks.onAdminFacts(packet));
    this.connection.on('triggers', (packet) => this.callbacks.onTriggers(packet));

    // A shot the world asked for. The camera stops following and looks at a spot
    // for a moment; anything the player does takes it straight back, which is
    // why the ending is decided here and not on the server.
    this.connection.on('openMenu', (packet) => {
      this.callbacks.onMenu(packet);
    });

    this.connection.on('holdCamera', (packet) => {
      if (packet.tenths <= 0) {
        this.heldShot = null;
        return;
      }

      this.heldShot = { until: performance.now() + packet.tenths * 100 };
      this.lookAt(packet.tx, packet.tz);
    });
    this.connection.on('dropTable', (packet) => this.callbacks.onDropTable(packet));
    this.connection.on('gathering', (packet) => this.callbacks.onGathering(packet));
    this.connection.on('worldCheck', (packet) => this.callbacks.onWorldCheck(packet));
    this.connection.on('blink', () => this.callbacks.onBlink());
    this.connection.on('dialogue', (packet) => this.callbacks.onDialogue(packet));
    this.connection.on('progress', (packet) => this.callbacks.onProgress(packet));
    this.connection.on('zones', (packet) => {
      this.scene.setZones(packet.zones);
      this.callbacks.onZones(packet);
    });
    this.connection.on('spawns', (packet) => this.callbacks.onSpawns(packet));
    this.connection.on('shopStock', (packet) => this.callbacks.onShopStock(packet));

    this.connection.on('worldPatch', (packet) => {
      this.state.applyPatch(packet);
      this.scene.applyPatch(packet);

      // Something on a tile may have just come or gone, so what was remembered
      // about the tile under the pointer is no longer worth anything.
      this.floatingAt = null;
    });

    this.connection.on('bank', (open) => this.callbacks.onBank(open));
    this.connection.on('shop', (packet) => this.callbacks.onShop(packet));

    this.connection.on('bonuses', (packet) => {
      this.reach = Math.max(1, packet.reach);
      this.callbacks.onBonuses(packet);
    });

    this.connection.on('skill', (packet) => {
      // What was just earned, which is the difference rather than the total: the
      // wire carries where somebody is up to, and what anybody wants to see is
      // what they just got for the swing they just took.
      const before = this.state.experience[packet.skill] ?? 0;
      const wasLevel = this.state.levels[packet.skill] ?? 1;

      this.state.experience[packet.skill] = packet.experience;
      this.state.levels[packet.skill] = packet.level;

      const gained = Math.max(0, Math.round(packet.experience - before));
      if (packet.level > wasLevel && before > 0) sounds.cue('level-up');
      if (gained > 0 || packet.level > wasLevel) {
        this.callbacks.onTrained({
          skill: packet.skill,
          gained,
          level: packet.level,
          levelled: packet.level > wasLevel,
        });
      }

      this.callbacks.onSkills(Array.from(this.state.levels), Array.from(this.state.experience));
    });

    this.connection.on('appearance', (packet) => {
      const entity = this.state.entities.get(packet.entityId);
      if (entity) {
        entity.equipment = packet.equipment;
        entity.look = packet.look;
        this.scene.dressEntity(packet.entityId, packet.look);
        return;
      }

      // Their own change comes back the same way, so what they chose and what
      // they are drawn with cannot drift apart.
      if (this.state.ready) {
        this.state.localLook = packet.look;
        this.scene.dressLocal(packet.look);
        this.callbacks.onLook(packet.look);
      }
    });

    this.connection.on('animation', (packet) => {
      if (packet.pose === EntityPose.Bend) this.scene.showBend(packet.entityId);
      if (packet.pose === EntityPose.PickUp) this.scene.showPickUp(packet.entityId);
    });

    this.connection.on('hit', (packet) => {
      // Their own health drives the interface; everybody's drives the bar over
      // their head and the number that floats off it.
      if (packet.entityId === this.state.localId) {
        this.callbacks.onHealth(packet.health, packet.maxHealth);
      }

      this.scene.showHit(
        packet.entityId,
        packet.damage,
        packet.health,
        packet.maxHealth,
        packet.fromId,
      );
      if (packet.fromId > 0) this.scene.showSwing(packet.fromId);
      this.hitSounds(packet.entityId, packet.fromId, packet.damage);

      // A blow of their own says what they are fighting, whatever they were
      // walking towards a moment ago. The server decides who is fighting whom;
      // this is the client hearing about it, turning to look, and keeping up
      // with it if it moves.
      if (packet.fromId === this.state.localId && packet.entityId !== this.state.localId) {
        this.facing = { entityId: packet.entityId };
        this.fighting = packet.entityId;
        this.fightTile = null;
        this.lastBlow = performance.now();
      }

      // Being hit by whatever is being fought counts as the fight carrying on.
      if (packet.entityId === this.state.localId && packet.fromId === this.fighting) {
        this.lastBlow = performance.now();
      }
    });

    // What time the world thinks it is. Kept rather than acted on: the sky is
    // moved every frame from the clock, not from the packet.
    this.connection.on('timeOfDay', (packet) => {
      this.clock = {
        dayMs: Math.max(1000, packet.dayMs),
        // The difference between their clock and ours, so a machine set wrong
        // still sees the same sky as everybody else.
        drift: packet.now - Date.now(),
        forced: packet.forced,
      };
    });

    // Something crossing the ground between two fighters. Nothing waits for it
    // and nothing depends on it: the blow has already been dealt.
    this.connection.on('flying', (packet) => {
      // A spell is drawn the way the book says, which the tools may have
      // changed since the world started.
      const spell = packet.spell > 0 ? this.spellbook.get(packet.spell) : undefined;
      const projectile = spell?.projectile;
      const model = projectile?.model
        ? this.modelLibrary.find((one) => one.name === projectile.model)
        : undefined;
      this.scene.showFlying(
        packet.fromId,
        packet.toId,
        packet.kind,
        packet.tenths,
        projectile
          ? {
              colour: projectile.colour,
              colour2: projectile.colour2,
              size: projectile.size,
              arc: projectile.arc,
              model,
            }
          : undefined,
        packet.toId === 0 ? { tx: packet.tx, tz: packet.tz } : null,
      );

      // Heard as it leaves and again as it lands, which only the client knows
      // the moment of.
      const from = this.placeOf(packet.fromId);
      if (spell) {
        if (spell.sounds.cast) sounds.sound(spell.sounds.cast, from);
        else sounds.cue('cast', from);
        window.setTimeout(() => {
          const landed =
            packet.toId > 0
              ? this.placeOf(packet.toId)
              : { x: packet.tx + 0.5, z: packet.tz + 0.5 };
          if (spell.sounds.impact) sounds.sound(spell.sounds.impact, landed);
          else sounds.cue('impact', landed);
        }, packet.tenths * 100);
      } else {
        sounds.cue('shoot', from);
      }
    });

    this.connection.on('soundLibrary', (json) => sounds.setLibrary(json));

    this.connection.on('playSound', (packet) => {
      const at = (packet.entityId > 0 ? this.placeOf(packet.entityId) : null) ?? {
        x: packet.tx + 0.5,
        z: packet.tz + 0.5,
      };
      if (packet.cue) sounds.cue(packet.name, at);
      else sounds.sound(packet.name, at);
    });

    this.connection.on('spellbook', (json) => {
      try {
        const book = JSON.parse(json) as { spells: SpellWire[] };
        this.spellbook = new Map(book.spells.map((one) => [one.id, one]));
      } catch {
        this.spellbook = new Map();
      }
    });

    this.connection.on('crafting', (packet) => {
      this.callbacks.onCrafting(
        packet.open
          ? {
              objectId: packet.objectId,
              name: packet.name,
              tx: packet.tx,
              tz: packet.tz,
              plane: packet.plane,
            }
          : null,
      );
    });

    this.connection.on('died', (entityId) => {
      const entity = this.state.entities.get(entityId);
      if (entityId === this.state.localId) sounds.cue('death');
      else if (entity?.kind === WireEntityKind.Npc) {
        sounds.npcSound(entity.defId, 'die', 'npc-die', this.placeOf(entityId));
      } else sounds.cue('death', this.placeOf(entityId), 0.6);
      this.scene.showDeath(entityId);
      // Nothing to keep up with any more, whether it was them or us.
      if (entityId === this.fighting || entityId === this.state.localId) this.fighting = null;
    });

    this.connection.on('groundItems', (items) => {
      for (const pile of items) {
        this.groundItems.set(pile.id, pile);
        this.scene.addGroundItem(pile);
      }
    });

    this.connection.on('groundItemsGone', (ids) => {
      for (const id of ids) {
        this.groundItems.delete(id);
        this.scene.removeGroundItem(id);
      }
    });

    this.connection.on('container', (packet) => {
      const id = packet.container as ContainerIdValue;
      const slots = this.container(id);
      if (packet.full) slots.fill(null);

      for (const entry of packet.slots) {
        if (entry.slot < 0 || entry.slot >= slots.length) continue;
        slots[entry.slot] =
          entry.itemId === 0 ? null : { id: entry.itemId, quantity: entry.quantity };
      }

      this.callbacks.onContainer(id, slots);
    });
    this.connection.on('devotion', (packet) => {
      this.bound = packet.bound;
      const now = new Set(packet.prayers);
      if ([...now].some((id) => !this.heldPrayers.has(id))) sounds.cue('prayer-on');
      else if ([...this.heldPrayers].some((id) => !now.has(id))) sounds.cue('prayer-off');
      this.heldPrayers = now;
    });
    this.connection.on('vitals', (packet) => {
      this.state.local.runEnergy = packet.runEnergy;

      // What the server has run set to, unless it was switched here a moment ago
      // and this packet left before the server heard about that.
      if (performance.now() - this.toggledRunAt > 1000) this.state.local.running = packet.running;

      this.callbacks.onRunEnergy(packet.runEnergy, this.state.local.running);
      this.callbacks.onMana(packet.mana, packet.maxMana);
    });
  }
}

/** How loud each kind of sound is, as somebody set it. */
function hearWith(settings: GameSettings): void {
  sounds.setVolumes({
    master: settings.soundVolume,
    effects: settings.effectsVolume,
    interface: settings.interfaceVolume,
    ambient: settings.ambientVolume,
    muted: settings.muted,
  });
}

/**
 * Every tile on the straight line between two, not counting the first.
 *
 * A pointer moving quickly reports one position every frame and skips whatever
 * was under it in between, so a trace made only of what was reported has holes
 * in it. Bresenham fills them.
 */
function tilesBetween(
  from: { tx: number; tz: number },
  to: { tx: number; tz: number },
): { tx: number; tz: number }[] {
  const tiles: { tx: number; tz: number }[] = [];

  let x = from.tx;
  let z = from.tz;
  const stepX = to.tx > from.tx ? 1 : -1;
  const stepZ = to.tz > from.tz ? 1 : -1;
  const spanX = Math.abs(to.tx - from.tx);
  const spanZ = Math.abs(to.tz - from.tz);

  // Nothing sensible to draw between two tiles that are miles apart: the pointer
  // left the world and came back somewhere else.
  if (spanX + spanZ > 64) return tiles;

  let error = spanX - spanZ;
  while (x !== to.tx || z !== to.tz) {
    const doubled = error * 2;
    if (doubled > -spanZ) {
      error -= spanZ;
      x += stepX;
    }
    if (doubled < spanX) {
      error += spanX;
      z += stepZ;
    }

    tiles.push({ tx: x, tz: z });
  }

  return tiles;
}

/**
 * How light a named stretch of day is, for a world being held at one.
 *
 * The middle of the stretch rather than its edge, so holding it at dusk looks
 * like dusk rather than like the moment dusk began.
 */
function daylightForPhase(phase: string): number {
  switch (phase) {
    case 'night':
      return daylight(0.85);
    case 'dusk':
      return daylight(0.65);
    case 'dawn':
      return daylight(0.05);
    default:
      return 1;
  }
}
